import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  Button,
  Progress,
  message,
  Spin,
  Tooltip,
  Modal,
  Checkbox,
  Alert,
  Tag,
  Typography,
  Select,
  Upload,
  Row,
  Col,
} from "antd";
import {
  UserAddOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FormOutlined,
  PlusOutlined,
  UploadOutlined,
  SendOutlined,
  CalendarOutlined,
  LinkOutlined,
  HomeOutlined,
  FilterOutlined,
  InboxOutlined,
  TeamOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
  RightOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { propertyKeys } from "../../lib/queryKeys";
import {
  getDraftTenants,
  triggerScreening,
  generateAndSendLease,
  addApplicantDocument,
  removeApplicantDocument,
  markApplicantLeaseSigned,
  deleteLeaseUp,
} from "../../lib/api/onboardingApi";
import listingModel from "../../lib/firebase/listingModel";
import unitModel from "../../lib/firebase/unitModel";
import { StatCard } from "../../components/shared";
import { device } from "../../lib/breakpoints";
import { useMedia } from "react-use";
import { getDebugMode } from "../../helpers/constants";
import { getUnitDisplayAddress } from "../../lib/unitAddressUtils";
import { trackProduct } from "../../lib/analytics/trackProduct";

const DOC_LABELS = { photo_id: "Photo ID", paystub: "Paystubs", w2: "W2" };
const DOC_GALLERY_ORDER = ["photo_id", "paystub", "w2"];

function isLikelyImageDoc(doc) {
  const name = (doc?.name || "").toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(name)) return true;
  const path = (doc?.path || "").toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(path)) return true;
  let url = (doc?.url || "").toLowerCase();
  try {
    url = decodeURIComponent(url);
  } catch {
    /* keep raw */
  }
  return /\.(png|jpe?g|gif|webp|bmp|heic|heif)(\?|#|$)/i.test(url);
}

function formatCurrencyUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function formatApplicantAddress(tenant) {
  const line1 = tenant.currentStreet || tenant.address || "";
  const line2 = [tenant.currentCity, tenant.currentState, tenant.currentZip]
    .filter(Boolean)
    .join(", ")
    .replace(/,\s*,/g, ",")
    .trim();
  if (line1 && line2) return `${line1}, ${line2}`;
  return line1 || line2 || null;
}

/** Fields filled by the public /apply form (not tour/prospect-only rows). */
function getApplicationDetailRows(tenant) {
  if (!tenant || tenant.ingestion_status === "prospect") return [];
  const address = formatApplicantAddress(tenant);
  const income = formatCurrencyUsd(tenant.monthlyGrossIncome);
  const rows = [
    { label: "Phone", value: tenant.phone || null },
    { label: "Email", value: tenant.email || tenant.tenantEmail || null },
    { label: "SSN (last 4)", value: tenant.ssnLast4 ? `•••-••-${tenant.ssnLast4}` : null },
    { label: "Current address", value: address },
    { label: "Employer", value: tenant.employerName || null },
    { label: "Job title", value: tenant.jobTitle || null },
    { label: "Monthly gross income", value: income },
    {
      label: "Previous landlord",
      value: tenant.previousLandlordName || null,
    },
    {
      label: "Previous landlord phone",
      value: tenant.previousLandlordPhone || null,
    },
    {
      label: "Previous landlord email",
      value: tenant.previousLandlordEmail || null,
    },
    {
      label: "Landlord lawsuit history",
      value:
        tenant.hasLandlordLawsuit === true
          ? "Yes"
          : tenant.hasLandlordLawsuit === false
            ? "No"
            : null,
    },
    {
      label: "Lawsuit details",
      value:
        tenant.hasLandlordLawsuit === true
          ? tenant.landlordLawsuitDetails || null
          : null,
    },
    {
      label: "Background-check consent",
      value:
        tenant.consentBackgroundCheck === true
          ? "Yes"
          : tenant.consentBackgroundCheck === false
          ? "No"
          : null,
    },
  ];
  return rows.filter((r) => r.value);
}

/** Demo doors so the door filter works with ?debug=true sample rows. */
const DEMO_UNITS_FOR_FILTER = [
  {
    id: "demo-unit-a",
    name: "Lower Left",
    address: "123 Maple St",
    address_city: "Springfield",
    address_state: "MO",
    photos: [
      "/images/lease-up/lease-up-pipeline.png",
      "/images/lease-up/lease-up-review.png",
    ],
  },
  {
    id: "demo-unit-b",
    name: "Unit 101",
    address: "456 Oak Ave",
    address_city: "Springfield",
    address_state: "MO",
    photos: ["/images/lease-up/lease-up-review.png"],
  },
];

/** All lease-up media: application docs (ID, paystubs, W-2, …) then listing/door photos. */
function resolveApplicantGallery(
  tenant,
  { listingsById = {}, unitsById = {} } = {}
) {
  if (!tenant) return [];
  const slides = [];
  const seen = new Set();

  const pushSlide = (slide) => {
    if (!slide?.url || seen.has(slide.url)) return;
    seen.add(slide.url);
    slides.push(slide);
  };

  const docs = Array.isArray(tenant.uploadedDocuments)
    ? [...tenant.uploadedDocuments]
    : [];
  docs.sort((a, b) => {
    const ai = DOC_GALLERY_ORDER.indexOf(a?.type);
    const bi = DOC_GALLERY_ORDER.indexOf(b?.type);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  docs.forEach((d, i) => {
    if (!d?.url) return;
    const typeLabel = DOC_LABELS[d.type] || d.type || "Document";
    const isImage = isLikelyImageDoc(d);
    pushSlide({
      url: d.url,
      kind: isImage ? "image" : "document",
      label: d.name || `${typeLabel} ${i + 1}`,
      typeLabel,
    });
  });

  const listing = tenant.listingId ? listingsById[tenant.listingId] : null;
  const unitId = resolveTenantUnitId(tenant, listingsById);
  const unit = unitId ? unitsById[unitId] : null;
  const listingPhotos = Array.isArray(listing?.photos)
    ? listing.photos.filter(Boolean)
    : [];
  const unitPhotos = Array.isArray(unit?.photos)
    ? unit.photos.filter(Boolean)
    : [];

  [...listingPhotos, ...unitPhotos].forEach((url, i) => {
    pushSlide({
      url,
      kind: "image",
      label: `Listing photo ${i + 1}`,
      typeLabel: "Listing",
    });
  });

  return slides;
}

/** Image URLs only (card thumbs); docs ordered photo ID → paystubs → W-2 first. */
function resolveApplicantPhotos(tenant, ctx = {}) {
  return resolveApplicantGallery(tenant, ctx)
    .filter((s) => s.kind === "image")
    .map((s) => s.url);
}

function ApplicantPhotoCarousel({
  photos,
  slides: slidesProp,
  label,
  photoKey,
  hero = false,
}) {
  const slides =
    Array.isArray(slidesProp) && slidesProp.length > 0
      ? slidesProp
      : (Array.isArray(photos) ? photos : []).map((url) => ({
          url,
          kind: "image",
          label: label || "Photo",
          typeLabel: "Photo",
        }));
  const [index, setIndex] = useState(0);
  const count = slides.length;
  const safeIndex = count > 0 ? Math.min(index, count - 1) : 0;
  const current = count > 0 ? slides[safeIndex] : null;
  const key = `${photoKey}:${slides.map((s) => s.url).join("|")}`;

  useEffect(() => {
    setIndex(0);
  }, [key]);

  if (!current) return null;

  const goPrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIndex((i) => (i - 1 + count) % count);
  };

  const goNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIndex((i) => (i + 1) % count);
  };

  const openCurrent = () => {
    if (current?.url) window.open(current.url, "_blank", "noopener,noreferrer");
  };

  return (
    <ApplicantCarouselWrap
      $hero={hero}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <ApplicantCarouselFrame $hero={hero}>
        {current.kind === "document" ? (
          <ApplicantCarouselDoc
            type="button"
            $hero={hero}
            onClick={openCurrent}
            aria-label={`Open ${current.label || current.typeLabel}`}
          >
            <FileTextOutlined />
            <ApplicantCarouselDocType>
              {current.typeLabel || "Document"}
            </ApplicantCarouselDocType>
            <ApplicantCarouselDocName>
              {current.label || "Open file"}
            </ApplicantCarouselDocName>
            <ApplicantCarouselDocHint>Tap to open</ApplicantCarouselDocHint>
          </ApplicantCarouselDoc>
        ) : (
          <ApplicantCarouselImg
            $hero={hero}
            src={current.url}
            alt={`${current.label || label || "Door"} ${safeIndex + 1}`}
            onClick={openCurrent}
          />
        )}
        {current.typeLabel ? (
          <ApplicantCarouselBadge aria-hidden>
            {current.typeLabel}
          </ApplicantCarouselBadge>
        ) : null}
        {count > 1 && (
          <>
            <ApplicantCarouselNav
              type="button"
              $side="left"
              onClick={goPrev}
              aria-label="Previous"
            >
              <LeftOutlined />
            </ApplicantCarouselNav>
            <ApplicantCarouselNav
              type="button"
              $side="right"
              onClick={goNext}
              aria-label="Next"
            >
              <RightOutlined />
            </ApplicantCarouselNav>
            <ApplicantCarouselCounter aria-hidden>
              {safeIndex + 1} / {count}
            </ApplicantCarouselCounter>
          </>
        )}
      </ApplicantCarouselFrame>
    </ApplicantCarouselWrap>
  );
}

function formatLeaseUpDate(iso) {
  if (!iso) return null;
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

/** When the applicant entered Lease-Ups (apply / inquiry). */
function formatApplicantEntryWhen(tenant) {
  const formatted = formatLeaseUpDate(tenant?.createdAt);
  if (!formatted) return null;
  if (tenant.ingestion_status === "prospect") {
    return `Inquired ${formatted}`;
  }
  return `Applied ${formatted}`;
}

function doorOptionLabel(unit) {
  if (!unit) return "";
  const name = unit.name || unit.unitNumber || null;
  const addr = getUnitDisplayAddress(unit);
  if (name && addr) {
    const nameLower = String(name).toLowerCase();
    if (addr.toLowerCase().includes(nameLower)) return addr;
    return `${name} · ${addr}`;
  }
  return name || addr || unit.id || "";
}

function getDoorPhotoUrl(unit) {
  if (Array.isArray(unit?.photos) && unit.photos[0]) return unit.photos[0];
  return null;
}

function DoorFilterOptionContent({ unit = null, label }) {
  const photoUrl = getDoorPhotoUrl(unit);
  return (
    <DoorOptionRow>
      {photoUrl ? (
        <DoorOptionThumb aria-hidden>
          <img src={photoUrl} alt="" />
        </DoorOptionThumb>
      ) : (
        <DoorOptionPlaceholder aria-hidden>
          <HomeOutlined />
        </DoorOptionPlaceholder>
      )}
      <DoorOptionText>{label}</DoorOptionText>
    </DoorOptionRow>
  );
}

/** Door / unit id an applicant is applying for (unit → listing.unitId). */
function resolveTenantUnitId(tenant, listingsById = {}) {
  if (!tenant) return null;
  if (tenant.unitId) return tenant.unitId;
  if (tenant.listingId && listingsById[tenant.listingId]?.unitId) {
    return listingsById[tenant.listingId].unitId;
  }
  return null;
}

/** Door / unit label for an applicant (unit → listing). */
function resolveApplicantDoorLabel(
  tenant,
  { unitsById = {}, listingsById = {} } = {}
) {
  if (!tenant) return null;

  let unit = tenant.unitId ? unitsById[tenant.unitId] : null;
  const listing = tenant.listingId ? listingsById[tenant.listingId] : null;
  if (!unit && listing?.unitId) {
    unit = unitsById[listing.unitId] || null;
  }

  if (unit) {
    return doorOptionLabel(unit) || null;
  }

  if (listing) {
    const parts = [listing.address, listing.city, listing.state].filter(Boolean);
    if (parts.length) return parts.join(", ");
    if (listing.headline || listing.title) {
      return listing.headline || listing.title;
    }
  }

  return null;
}

function buildLeaseUpActivityRows(tenant) {
  const rows = [];
  const push = (key, label, atRaw) => {
    if (!atRaw) return;
    const formatted = formatLeaseUpDate(atRaw);
    if (!formatted) return;
    const sort = new Date(atRaw).getTime();
    if (Number.isNaN(sort)) return;
    rows.push({ key, label, at: formatted, sort });
  };
  push(
    "applied",
    tenant.ingestion_status === "prospect" ? "Inquiry received" : "Applied",
    tenant.createdAt
  );
  push(
    "pkg",
    "Application package emailed",
    tenant.application_package_email_sent_at
  );
  push("fee", "Screening fee paid", tenant.application_fee_paid_at);
  push(
    "feeEmail",
    "Screening fee email sent",
    tenant.screening_fee_email_sent_at
  );
  push("inv", "Screening invite sent", tenant.screening_invite_sent_at);
  push("rep", "Screening report received", tenant.screening_report_received_at);
  push("lsent", "Lease sent for signature", tenant.leaseSentForSignatureAt);
  push("lsign", "Lease signed", tenant.leaseSignedAt);
  const docs = tenant.uploadedDocuments;
  if (Array.isArray(docs)) {
    docs.forEach((d, i) => {
      const label = `${DOC_LABELS[d.type] || d.type || "Document"} uploaded`;
      push(`doc-${i}`, label, d.uploadedAt);
    });
  }
  rows.sort((a, b) => b.sort - a.sort);
  return rows.map(({ key, label, at }) => ({ key, label, at }));
}

function toActivityMillis(raw) {
  if (!raw) return 0;
  try {
    if (typeof raw?.toDate === "function") {
      const d = raw.toDate();
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    }
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  } catch {
    return 0;
  }
}

/** Most recent activity timestamp for lease-up sorting. */
function getApplicantLastActivityAt(tenant) {
  if (!tenant) return 0;
  const candidates = [
    tenant.updatedAt,
    tenant.createdAt,
    tenant.application_package_email_sent_at,
    tenant.application_fee_paid_at,
    tenant.leaseSentForSignatureAt,
    tenant.leaseSignedAt,
    tenant.screening_invite_sent_at,
    tenant.screening_report_received_at,
  ];
  if (Array.isArray(tenant.uploadedDocuments)) {
    tenant.uploadedDocuments.forEach((d) => {
      if (d?.uploadedAt) candidates.push(d.uploadedAt);
    });
  }
  return candidates.reduce((max, raw) => Math.max(max, toActivityMillis(raw)), 0);
}

function getApplicantDisplayName(tenant) {
  return (
    tenant?.name ||
    [tenant?.firstName, tenant?.lastName].filter(Boolean).join(" ") ||
    tenant?.email ||
    ""
  );
}

const LEASE_UP_SORT_OPTIONS = [
  { value: "activity", label: "Last activity" },
  { value: "pipeline", label: "Pipeline stage" },
  { value: "newest", label: "Newest applicants" },
  { value: "name", label: "Name (A-Z)" },
];

const STEP_TOURS = "Tours/Inquiries";
const STEP_APPLIED = "Applied";
const STEP_CHECK = "Background check";
const STEP_SCREENED = "Screened";
const STEP_SIGNED = "Lease Signed";

const PIPELINE_SORT_ORDER = {
  [STEP_TOURS]: 0,
  [STEP_APPLIED]: 1,
  [STEP_CHECK]: 2,
  [STEP_SCREENED]: 3,
  [STEP_SIGNED]: 4,
};

function getStep(tenant) {
  const isProspect = tenant.ingestion_status === "prospect";
  if (isProspect)
    return {
      step: STEP_TOURS,
      percent: 0,
      sub:
        tenant.sub_status === "tour_scheduled" ? "Tour scheduled" : "Inquiry",
    };

  const leaseActive =
    tenant.leaseStatus === "active" || !!tenant.lease_manually_marked_signed_at;
  const leasePending = tenant.leaseStatus === "pending_signature";
  const inviteSent = !!tenant.screening_invite_sent_at;
  const screeningPassed = tenant.screening_status === "passed";
  const screeningFailed = tenant.screening_status === "failed";
  const screeningPending = tenant.screening_status === "pending";
  const feeEmailSent = !!tenant.screening_fee_email_sent_at;
  const feePaid = tenant.application_fee_paid === true;
  const screeningDone = screeningPassed || screeningFailed || screeningPending;

  if (leaseActive) return { step: STEP_SIGNED, percent: 100 };
  if (leasePending)
    return { step: STEP_SIGNED, percent: 80, sub: "Awaiting signature" };
  if (screeningDone) {
    return {
      step: STEP_SCREENED,
      percent: 75,
      sub: screeningPending ? "Screening pending" : null,
    };
  }
  if (inviteSent || feePaid || feeEmailSent) {
    return {
      step: STEP_CHECK,
      percent: 50,
      sub: inviteSent
        ? "Invite sent"
        : feePaid
          ? "Fee paid"
          : "Awaiting fee payment",
    };
  }
  return { step: STEP_APPLIED, percent: 35, sub: null };
}

const PIPELINE_ORDER = [
  STEP_TOURS,
  STEP_APPLIED,
  STEP_CHECK,
  STEP_SCREENED,
  STEP_SIGNED,
];

const PIPELINE_STEP_META = [
  { key: STEP_TOURS, short: "Tour / inquiry", Icon: CalendarOutlined },
  { key: STEP_APPLIED, short: "Applied", Icon: FormOutlined },
  { key: STEP_CHECK, short: "Check", Icon: UserAddOutlined },
  { key: STEP_SCREENED, short: "Screened", Icon: ClockCircleOutlined },
  { key: STEP_SIGNED, short: "Signed", Icon: CheckCircleOutlined },
];

function compareLeaseUpApplicants(a, b, sortKey) {
  if (sortKey === "activity") {
    return getApplicantLastActivityAt(b) - getApplicantLastActivityAt(a);
  }
  if (sortKey === "newest") {
    return toActivityMillis(b.createdAt) - toActivityMillis(a.createdAt);
  }
  if (sortKey === "name") {
    return getApplicantDisplayName(a).localeCompare(
      getApplicantDisplayName(b),
      undefined,
      { sensitivity: "base" }
    );
  }
  // pipeline (default): stage first, then most recent activity within stage
  const stepA = getStep(a).step;
  const stepB = getStep(b).step;
  const stageDiff =
    (PIPELINE_SORT_ORDER[stepA] ?? 5) - (PIPELINE_SORT_ORDER[stepB] ?? 5);
  if (stageDiff !== 0) return stageDiff;
  return getApplicantLastActivityAt(b) - getApplicantLastActivityAt(a);
}

/** What to do next in the UI (drives copy + which buttons we emphasize). */
function getApplicantActionState(tenant) {
  if (tenant.ingestion_status === "prospect") return { kind: "prospect" };

  if (tenant.leaseStatus === "active" || tenant.lease_manually_marked_signed_at)
    return { kind: "done" };
  if (tenant.leaseStatus === "pending_signature") return { kind: "lease_out" };

  const inviteSent = !!tenant.screening_invite_sent_at;
  const feePaid = tenant.application_fee_paid === true;
  const s = tenant.screening_status;

  if (!inviteSent && !feePaid) return { kind: "invite_screen" };
  if (!inviteSent && feePaid) return { kind: "screening_starting" };
  if (inviteSent && (s == null || s === undefined || s === "pending"))
    return { kind: "screening_pending" };
  if (s === "failed") return { kind: "screening_failed" };

  return { kind: "ready_lease" };
}

/** Shown when `?debug=true` on Lease-Ups so the pipeline UI is previewable without API data */
const LEASE_UPS_DEMO_TENANTS = [
  {
    id: "leaseups-demo-prospect",
    name: "Jordan Lee",
    email: "jordan.lee@example.com",
    ingestion_status: "prospect",
    listingId: "demo-listing-preview",
    unitId: "demo-unit-a",
    sub_status: "tour_scheduled",
  },
  {
    id: "leaseups-demo-applied",
    name: "Sam Rivera",
    email: "sam.rivera@example.com",
    phone: "(555) 201-8844",
    ssnLast4: "4281",
    currentStreet: "88 Cedar Lane",
    currentCity: "Springfield",
    currentState: "MO",
    currentZip: "65802",
    employerName: "Midwest Logistics",
    jobTitle: "Operations coordinator",
    monthlyGrossIncome: 5200,
    previousLandlordName: "Pat Nguyen",
    previousLandlordPhone: "(555) 310-2290",
    previousLandlordEmail: "pat.nguyen@example.com",
    hasLandlordLawsuit: false,
    consentBackgroundCheck: true,
    ingestion_status: "applied",
    listingId: "demo-listing-preview",
    unitId: "demo-unit-a",
  },
  {
    id: "leaseups-demo-invite-sent",
    name: "Taylor Brooks",
    email: "taylor.brooks@example.com",
    phone: "(555) 418-7721",
    ssnLast4: "9033",
    currentStreet: "14 Birch Ct",
    currentCity: "Springfield",
    currentState: "MO",
    currentZip: "65804",
    employerName: "City Clinic",
    jobTitle: "Medical assistant",
    monthlyGrossIncome: 4100,
    hasLandlordLawsuit: false,
    consentBackgroundCheck: true,
    ingestion_status: "applied",
    screening_status: null,
    screening_invite_sent_at: "2025-01-16T10:00:00.000Z",
    leaseStatus: null,
    unitId: "demo-unit-b",
    uploadedDocuments: [],
  },
  {
    id: "leaseups-demo-screened",
    name: "Alex Morgan",
    email: "alex.morgan@example.com",
    phone: "(555) 902-1145",
    ssnLast4: "7712",
    currentStreet: "502 Willow Rd",
    currentCity: "Springfield",
    currentState: "MO",
    currentZip: "65806",
    employerName: "Northside Credit Union",
    jobTitle: "Branch associate",
    monthlyGrossIncome: 4800,
    previousLandlordName: "Riverbend Rentals",
    previousLandlordPhone: "(555) 220-0188",
    hasLandlordLawsuit: false,
    consentBackgroundCheck: true,
    ingestion_status: "applied",
    screening_status: "passed",
    screening_invite_sent_at: "2025-01-11T11:00:00.000Z",
    screening_report_received_at: "2025-01-12T16:45:00.000Z",
    leaseStatus: null,
    unitId: "demo-unit-b",
    uploadedDocuments: [
      {
        type: "photo_id",
        url: "https://example.com/id.pdf",
        uploadedAt: "2025-01-09T12:00:00.000Z",
      },
    ],
  },
];

const OnboardingTab = ({ userId }) => {
  const isMobileLayout = useMedia(device.mobile);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightTenantId = searchParams.get("tenantId");
  const listItemRefs = useRef({});
  const queryClient = useQueryClient();
  const [loadingScreening, setLoadingScreening] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRefs = useRef({});
  const [onboardModalOpen, setOnboardModalOpen] = useState(false);
  const [onboardTenant, setOnboardTenant] = useState(null);
  const [onboardLeaseFile, setOnboardLeaseFile] = useState(null);
  const [onboardStep, setOnboardStep] = useState(0);
  const [onboardStatus, setOnboardStatus] = useState({
    step1: "pending",
    step2: "pending",
    step3: "pending",
  });
  const [onboardLegalAck, setOnboardLegalAck] = useState(false);
  const [detailTenantId, setDetailTenantId] = useState(
    () => highlightTenantId || null
  );
  const [leaseUpDoorFilter, setLeaseUpDoorFilter] = useState("all");
  const [leaseUpSort, setLeaseUpSort] = useState("activity");
  const [leaseUpsLayout, setLeaseUpsLayout] = useState(() => {
    if (typeof window === "undefined") return "grid";
    try {
      const saved = window.localStorage.getItem("lease_ups_layout");
      return saved === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return false;
    return window.matchMedia("(max-width: 900px)").matches;
  });

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const mediaQuery = window.matchMedia("(max-width: 900px)");
    const handler = (e) => setIsNarrowViewport(e.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, []);

  useEffect(() => {
    if (isNarrowViewport && leaseUpsLayout === "list") {
      setLeaseUpsLayout("grid");
    }
  }, [isNarrowViewport, leaseUpsLayout]);

  const setLeaseUpsLayoutPreference = (next) => {
    setLeaseUpsLayout(next);
    try {
      window.localStorage.setItem("lease_ups_layout", next);
    } catch {
      // ignore
    }
  };

  const isLeaseUpsDemo = getDebugMode();

  const { data, isLoading } = useQuery({
    queryKey: propertyKeys.onboardingDraftTenants(userId),
    queryFn: getDraftTenants,
    enabled: !!userId && !isLeaseUpsDemo,
  });

  const { data: listings = [] } = useQuery({
    queryKey: ["listings", "byLandlord", userId],
    queryFn: () => listingModel.getListingsByLandlord(userId),
    enabled: !!userId && !isLeaseUpsDemo,
  });

  const { data: units = [] } = useQuery({
    queryKey: ["units", "byOwner", "leaseUps", userId],
    queryFn: () => unitModel.getByOwner(userId),
    enabled: !!userId && !isLeaseUpsDemo,
  });

  const demoDraftPayload = { tenants: LEASE_UPS_DEMO_TENANTS };
  const effectiveDraft = isLeaseUpsDemo ? demoDraftPayload : data;
  const effectiveLoading = isLeaseUpsDemo ? false : isLoading;
  const hasListings = isLeaseUpsDemo ? true : (listings || []).length > 0;

  const doorsForFilter = isLeaseUpsDemo
    ? DEMO_UNITS_FOR_FILTER
    : units || [];

  const unitsById = useMemo(() => {
    const m = {};
    doorsForFilter.forEach((u) => {
      if (u?.id) m[u.id] = u;
    });
    return m;
  }, [doorsForFilter]);

  const listingsById = useMemo(() => {
    const m = {};
    (listings || []).forEach((l) => {
      if (l?.id) m[l.id] = l;
    });
    return m;
  }, [listings]);

  const tenants = useMemo(() => {
    const list = [...(effectiveDraft?.tenants || [])];
    list.sort((a, b) => compareLeaseUpApplicants(a, b, leaseUpSort));
    return list;
  }, [effectiveDraft?.tenants, leaseUpSort]);

  const filteredTenants = useMemo(() => {
    if (leaseUpDoorFilter === "all") return tenants;
    return tenants.filter(
      (t) => resolveTenantUnitId(t, listingsById) === leaseUpDoorFilter
    );
  }, [tenants, leaseUpDoorFilter, listingsById]);

  const leaseUpStats = useMemo(() => {
    const scopeTenants = filteredTenants;
    let inPipeline = scopeTenants.length;
    let tours = 0;
    let applied = 0;
    let awaitingSignature = 0;
    let readyToLease = 0;

    scopeTenants.forEach((t) => {
      const { step } = getStep(t);
      if (step === STEP_TOURS) tours += 1;
      else if (step === STEP_APPLIED) {
        applied += 1;
        const action = getApplicantActionState(t);
        if (action.kind === "ready_lease") readyToLease += 1;
        if (action.kind === "lease_out") awaitingSignature += 1;
      } else if (step === STEP_SIGNED) {
        const action = getApplicantActionState(t);
        if (action.kind === "lease_out") awaitingSignature += 1;
      }
    });

    return {
      inPipeline,
      tours,
      applied,
      readyToLease,
      awaitingSignature,
    };
  }, [filteredTenants]);

  useEffect(() => {
    if (!highlightTenantId) return;
    setDetailTenantId(highlightTenantId);
  }, [highlightTenantId]);

  useEffect(() => {
    if (!highlightTenantId || tenants.length === 0) return;
    const el = listItemRefs.current[highlightTenantId];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [highlightTenantId, tenants.length]);

  const detailTenant = useMemo(() => {
    if (!detailTenantId) return null;
    return (
      (effectiveDraft?.tenants || []).find((t) => t.id === detailTenantId) ||
      null
    );
  }, [detailTenantId, effectiveDraft?.tenants]);

  const demoActionBlocked = () => {
    if (!isLeaseUpsDemo) return false;
    message.info(
      "Demo mode: this action is disabled. Remove ?debug from the URL to use your real lease-up data."
    );
    return true;
  };

  const handleInviteToScreen = async (tenant) => {
    if (demoActionBlocked()) return;
    try {
      setLoadingScreening(tenant.id);
      const result = await triggerScreening(tenant.id);
      trackProduct("screening_invite_sent", {
        tenant_id: tenant.id,
        payment_required: Boolean(result?.paymentRequired),
        listing_id: tenant.listingId || null,
      });
      if (result?.paymentRequired) {
        message.success(
          "Applicant emailed a secure pay link. Screening starts automatically after they pay — you are not charged."
        );
      } else {
        message.success(
          "Screening invite emailed. They complete the background check from that link."
        );
      }
      queryClient.invalidateQueries({
        queryKey: propertyKeys.onboardingDraftTenants(userId),
      });
      queryClient.invalidateQueries({ queryKey: propertyKeys.tenants(userId) });
    } catch (e) {
      trackProduct("screening_invite_failed", {
        tenant_id: tenant?.id,
        error: e?.message || "unknown",
      });
      message.error(e.message || "Failed to trigger screening");
    } finally {
      setLoadingScreening(null);
    }
  };

  const openLeaseModal = (tenant) => {
    if (demoActionBlocked()) return;
    setOnboardTenant(tenant);
    setOnboardLeaseFile(null);
    setOnboardModalOpen(true);
    setOnboardStep(0);
    setOnboardStatus({ step1: "pending", step2: "pending", step3: "pending" });
    setOnboardLegalAck(false);
  };

  const handleSendLease = (tenant) => {
    openLeaseModal(tenant);
  };

  const handleMarkLeaseSigned = (tenant) => {
    if (demoActionBlocked()) return;
    if (!tenant?.id) return;
    const displayName =
      tenant.name ||
      [tenant.firstName, tenant.lastName].filter(Boolean).join(" ") ||
      tenant.email ||
      "this applicant";
    Modal.confirm({
      title: null,
      icon: null,
      centered: true,
      width: 380,
      zIndex: 11000,
      wrapClassName:
        "lo-compact-confirm lease-up-mark-signed-confirm-wrap",
      className: "lease-up-mark-signed-confirm",
      classNames: {
        mask: "lease-up-mark-signed-confirm-mask",
      },
      styles: {
        mask: { backgroundColor: "rgba(0, 0, 0, 0.45)" },
      },
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
            textAlign: "center",
            maxWidth: 320,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              fontSize: "2.5rem",
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CheckCircleOutlined
              aria-label="Confirm"
              style={{ color: "var(--color-primary, #2c5f2d)" }}
            />
          </div>
          <h3
            style={{
              fontFamily: "var(--font-primary)",
              fontSize: "1.125rem",
              fontWeight: 700,
              color: "var(--color-text)",
              margin: "0 0 0.75rem 0",
              lineHeight: "1.4",
            }}
          >
            Mark lease as signed?
          </h3>
          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--color-text-light)",
              margin: 0,
              lineHeight: "1.5",
              textWrap: "pretty",
            }}
          >
            Marks {displayName} as signed in Lease-Ups until e-signature is
            available.
          </p>
        </div>
      ),
      okText: "Mark as signed",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await markApplicantLeaseSigned(tenant.id);
          trackProduct("lease_marked_signed", {
            tenant_id: tenant.id,
            listing_id: tenant.listingId || null,
          });
          message.success("Lease marked as signed.");
          queryClient.invalidateQueries({
            queryKey: propertyKeys.onboardingDraftTenants(userId),
          });
          queryClient.invalidateQueries({
            queryKey: propertyKeys.tenants(userId),
          });
        } catch (e) {
          message.error(e.message || "Failed to mark lease signed");
          throw e;
        }
      },
    });
  };

  const handleStartOnboarding = () => {
    if (!onboardTenant || !onboardLegalAck || !onboardLeaseFile) return;
    runOnboardSequence(onboardTenant, onboardLeaseFile);
  };

  const runOnboardSequence = async (tenant, leaseFile) => {
    if (demoActionBlocked()) return;
    try {
      setOnboardStep(1);
      setOnboardStatus((s) => ({ ...s, step1: "process" }));
      await generateAndSendLease(tenant.id, leaseFile);
      setOnboardStatus((s) => ({
        ...s,
        step1: "finish",
        step2: "finish",
        step3: "finish",
      }));
      setOnboardStep(2);
      trackProduct("lease_sent_for_signature", {
        tenant_id: tenant.id,
        listing_id: tenant.listingId || null,
      });
      message.success("Lease sent for signature.");
      queryClient.invalidateQueries({
        queryKey: propertyKeys.onboardingDraftTenants(userId),
      });
      queryClient.invalidateQueries({ queryKey: propertyKeys.tenants(userId) });
      setTimeout(() => {
        setOnboardModalOpen(false);
        setOnboardTenant(null);
        setOnboardLeaseFile(null);
      }, 1500);
    } catch (e) {
      message.error(e.message || "Failed to send lease");
      setOnboardStatus((s) => ({ ...s, step1: "error" }));
    }
  };

  const handleDocumentUpload = async (tenant, docType, files) => {
    if (demoActionBlocked()) return;
    const fileList = Array.isArray(files)
      ? files.filter(Boolean)
      : files
      ? [files]
      : [];
    if (fileList.length === 0) return;

    const key = `${tenant.id}-${docType}`;
    try {
      setUploadProgress((p) => ({ ...p, [key]: 0 }));
      for (let i = 0; i < fileList.length; i += 1) {
        await addApplicantDocument(tenant.id, {
          type: docType,
          file: fileList[i],
        });
        setUploadProgress((p) => ({
          ...p,
          [key]: Math.round(((i + 1) / fileList.length) * 100),
        }));
      }
      message.success(
        fileList.length === 1
          ? `${DOC_LABELS[docType]} uploaded.`
          : `${fileList.length} ${DOC_LABELS[docType]} files uploaded.`
      );
      queryClient.invalidateQueries({
        queryKey: propertyKeys.onboardingDraftTenants(userId),
      });
    } catch (e) {
      message.error(e.message || "Upload failed");
    } finally {
      setUploadProgress((p) => ({ ...p, [key]: undefined }));
    }
  };

  const handleDocumentRemove = async (tenant, doc) => {
    if (demoActionBlocked()) return;
    if (!doc?.url) return;
    const removeKey = `remove-${tenant.id}-${doc.url}`;
    try {
      setUploadProgress((p) => ({ ...p, [removeKey]: 0 }));
      await removeApplicantDocument(tenant.id, { url: doc.url });
      message.success("Document removed.");
      queryClient.invalidateQueries({
        queryKey: propertyKeys.onboardingDraftTenants(userId),
      });
    } catch (e) {
      message.error(e.message || "Failed to remove document");
    } finally {
      setUploadProgress((p) => ({ ...p, [removeKey]: undefined }));
    }
  };

  const confirmDocumentRemove = (tenant, doc, label) => {
    if (demoActionBlocked()) return;
    if (!doc?.url) return;
    Modal.confirm({
      title: null,
      icon: null,
      centered: true,
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "2.5rem",
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ExclamationCircleOutlined
              aria-label="Warning"
              style={{ color: "var(--color-primary, #2c5f2d)" }}
            />
          </div>
          <h3
            style={{
              fontFamily: "var(--font-primary)",
              fontSize: "1.125rem",
              fontWeight: 700,
              color: "var(--color-text)",
              margin: "0 0 0.75rem 0",
              lineHeight: "1.4",
            }}
          >
            Remove this file?
          </h3>
          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--color-text-light)",
              margin: 0,
              lineHeight: "1.5",
              overflowWrap: "anywhere",
            }}
          >
            {label
              ? `"${label}" will be removed from this applicant.`
              : "This file will be removed from this applicant."}
          </p>
        </div>
      ),
      okText: "Remove file",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => handleDocumentRemove(tenant, doc),
    });
  };

  const handleDeleteLeaseUp = (tenant) => {
    if (demoActionBlocked()) return;
    if (!tenant?.id) return;
    const displayName =
      tenant.name ||
      [tenant.firstName, tenant.lastName].filter(Boolean).join(" ") ||
      tenant.email ||
      "this applicant";
    Modal.confirm({
      title: null,
      icon: null,
      centered: true,
      width: 380,
      zIndex: 11000,
      wrapClassName:
        "lo-compact-confirm lease-up-mark-signed-confirm-wrap",
      className: "lease-up-mark-signed-confirm",
      classNames: {
        mask: "lease-up-mark-signed-confirm-mask",
      },
      styles: {
        mask: { backgroundColor: "rgba(0, 0, 0, 0.45)" },
      },
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
            textAlign: "center",
            maxWidth: 320,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              fontSize: "2.5rem",
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ExclamationCircleOutlined
              aria-label="Warning"
              style={{ color: "var(--color-primary, #2c5f2d)" }}
            />
          </div>
          <h3
            style={{
              fontFamily: "var(--font-primary)",
              fontSize: "1.125rem",
              fontWeight: 700,
              color: "var(--color-text)",
              margin: "0 0 0.75rem 0",
              lineHeight: "1.4",
            }}
          >
            Delete this lease-up?
          </h3>
          <p
            style={{
              fontSize: "0.875rem",
              color: "var(--color-text-light)",
              margin: 0,
              lineHeight: "1.5",
              textWrap: "pretty",
            }}
          >
            Removes {displayName} from Lease-Ups. Their application record is
            archived and will no longer appear here.
          </p>
        </div>
      ),
      okText: "Delete lease-up",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteLeaseUp(tenant.id);
          trackProduct("lease_up_deleted", {
            tenant_id: tenant.id,
            listing_id: tenant.listingId || null,
          });
          setDetailTenantId(null);
          message.success("Lease-up deleted.");
          queryClient.invalidateQueries({
            queryKey: propertyKeys.onboardingDraftTenants(userId),
          });
        } catch (e) {
          message.error(e.message || "Failed to delete lease-up");
          throw e;
        }
      },
    });
  };

  if (effectiveLoading) {
    return (
      <OnboardingWrap className="lease-up-container">
        <Spin size="large" />
      </OnboardingWrap>
    );
  }

  return (
    <OnboardingWrap className="lease-up-container">
      {isLeaseUpsDemo && (
        <Alert
          type="info"
          showIcon
          closable
          message="Demo mode (debug URL)"
          description="Sample applicants show how each pipeline stage looks. Copy link still works for preview. Lease and uploads are disabled. Use ?debug=false or remove ?debug to see your real lease-up list."
          style={{ marginBottom: 16 }}
        />
      )}
      <LeaseUpStatsSection>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Tooltip title="People currently in your lease-up pipeline for this filter.">
                <div>
                  <StatCard
                    className="stats-card"
                    title="In pipeline"
                    value={leaseUpStats.inPipeline}
                    prefix={
                      <TeamOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                    }
                    valueStyle={{ color: "var(--color-primary)" }}
                  />
                </div>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Tooltip title="Early contacts and tour requests who have not finished the full application yet.">
                <div>
                  <StatCard
                    className="stats-card"
                    title="Tours / inquiries"
                    value={leaseUpStats.tours}
                    prefix={
                      <CalendarOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                    }
                    valueStyle={{ color: "var(--color-primary)" }}
                  />
                </div>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Tooltip title="Applications ready for your review. Send a lease when you are ready.">
                <div>
                  <StatCard
                    className="stats-card"
                    title="Ready to lease"
                    value={leaseUpStats.readyToLease}
                    prefix={
                      <FormOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                    }
                    valueStyle={{ color: "var(--color-primary)" }}
                  />
                </div>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Tooltip title="Leases sent and waiting on the applicant's signature.">
                <div>
                  <StatCard
                    className="stats-card"
                    title="Awaiting signature"
                    value={leaseUpStats.awaitingSignature}
                    prefix={
                      <FileTextOutlined
                        style={{ color: "var(--color-primary)" }}
                      />
                    }
                    valueStyle={{ color: "var(--color-primary)" }}
                  />
                </div>
              </Tooltip>
            </Col>
          </Row>
        </LeaseUpStatsSection>

        <LeaseUpApplicantsSection>
          {tenants.length > 0 && (
            <PropertyFilterRow>
              <LeaseUpToolbar>
                <PropertyFilterInner>
                  <ViewToggleWrap>
                    <Tooltip title="Card view">
                      <ViewToggleButton
                        type={leaseUpsLayout === "grid" ? "primary" : "default"}
                        icon={<AppstoreOutlined />}
                        onClick={() => setLeaseUpsLayoutPreference("grid")}
                        aria-label="Card view"
                      />
                    </Tooltip>
                    <Tooltip title="List view (desktop)">
                      <ViewToggleButton
                        type={leaseUpsLayout === "list" ? "primary" : "default"}
                        icon={<UnorderedListOutlined />}
                        onClick={() => setLeaseUpsLayoutPreference("list")}
                        aria-label="List view"
                      />
                    </Tooltip>
                  </ViewToggleWrap>
                  <PropertyFilterSelect
                    id="lease-up-sort"
                    aria-label="Sort applicants"
                    value={leaseUpSort}
                    onChange={setLeaseUpSort}
                    options={LEASE_UP_SORT_OPTIONS}
                    placeholder="Sort by"
                  />
                </PropertyFilterInner>
                <PropertyFilterInner $align="end">
                  <DoorFilterSelect
                    id="lease-up-door-filter"
                    aria-label="Filter by door"
                    value={leaseUpDoorFilter}
                    onChange={setLeaseUpDoorFilter}
                    options={[
                      { value: "all", label: "All doors" },
                      ...doorsForFilter.map((u) => ({
                        value: u.id,
                        label: doorOptionLabel(u),
                      })),
                    ]}
                    showSearch
                    optionFilterProp="label"
                    placeholder="Filter by door"
                    popupMatchSelectWidth
                    optionRender={(option) => {
                      const value = option?.value ?? option?.data?.value;
                      if (value === "all") {
                        return (
                          <DoorFilterOptionContent label="All doors" />
                        );
                      }
                      const unit = unitsById[value];
                      return (
                        <DoorFilterOptionContent
                          unit={unit}
                          label={
                            option?.label ||
                            option?.data?.label ||
                            doorOptionLabel(unit)
                          }
                        />
                      );
                    }}
                    labelRender={({ value, label }) => {
                      if (value === "all") {
                        return (
                          <DoorFilterOptionContent label="All doors" />
                        );
                      }
                      const unit = unitsById[value];
                      return (
                        <DoorFilterOptionContent
                          unit={unit}
                          label={
                            (typeof label === "string" && label) ||
                            doorOptionLabel(unit) ||
                            "Door"
                          }
                        />
                      );
                    }}
                  />
                </PropertyFilterInner>
              </LeaseUpToolbar>
            </PropertyFilterRow>
          )}
          {tenants.length > 0 && (
            <SectionEyebrow $variant="pipeline">
              {leaseUpDoorFilter === "all"
                ? `Applicants (${tenants.length})`
                : `Applicants (${filteredTenants.length} of ${tenants.length})`}
            </SectionEyebrow>
          )}
          {tenants.length === 0 ? (
            <PipelineEmptyShell>
              <PipelineEmptyCard>
                {hasListings ? (
                  <>
                    <PipelineEmptyIconWrap aria-hidden>
                      <LinkOutlined />
                    </PipelineEmptyIconWrap>
                    <PipelineEmptyTitle>
                      Share your application link
                    </PipelineEmptyTitle>
                    <PipelineEmptyLead>
                      Copy it from Listings and send to prospects—applicants
                      show up here.
                    </PipelineEmptyLead>
                    <PipelineEmptyActions>
                      <GetStartedButton
                        type="primary"
                        size="large"
                        icon={<LinkOutlined style={{ filter: "brightness(0) invert(1)" }} />}
                        onClick={() => navigate("/listings")}
                      >
                        Go to Listings
                      </GetStartedButton>
                    </PipelineEmptyActions>
                  </>
                ) : (
                  <>
                    <PipelineEmptyIconWrap aria-hidden>
                      <HomeOutlined />
                    </PipelineEmptyIconWrap>
                    <PipelineEmptyTitle>
                      Create a listing to start your lease-up pipeline
                    </PipelineEmptyTitle>
                    <PipelineEmptyLead>
                      This is where applicants live after they engage with your
                      vacancy, from tour requests and applications through a
                      signed lease. Add a Smart Vacancy listing first, then
                      share your link with prospects.
                    </PipelineEmptyLead>
                    <PipelineEmptyStepList>
                      <PipelineEmptyStep>
                        Create a listing for your unit (photos, rent, and
                        requirements).
                      </PipelineEmptyStep>
                      <PipelineEmptyStep>
                        Share the public listing link with qualified prospects.
                      </PipelineEmptyStep>
                      <PipelineEmptyStep>
                        Review applications (emailed to you), run your own
                        background checks, and send leases for e-signature.
                      </PipelineEmptyStep>
                    </PipelineEmptyStepList>
                    <PipelineEmptyActions>
                      <GetStartedButton
                        type="primary"
                        size="large"
                        icon={
                          <PlusOutlined
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        }
                        onClick={() => navigate("/listings")}
                      >
                        Create a listing
                      </GetStartedButton>
                    </PipelineEmptyActions>
                  </>
                )}
              </PipelineEmptyCard>
            </PipelineEmptyShell>
          ) : filteredTenants.length === 0 ? (
            <PipelineFilterEmpty>
              <PipelineFilterEmptyIcon aria-hidden>
                <FilterOutlined />
              </PipelineFilterEmptyIcon>
              <PipelineFilterEmptyTitle>
                No applicants for this door
              </PipelineFilterEmptyTitle>
              <PipelineFilterEmptyText>
                Try <strong>All doors</strong> to see everyone in your
                pipeline, or choose a different door from the filter above.
              </PipelineFilterEmptyText>
            </PipelineFilterEmpty>
          ) : leaseUpsLayout === "list" && !isNarrowViewport ? (
            <ApplicantListRows>
              {filteredTenants.map((tenant) => {
                const { step, percent, sub } = getStep(tenant);
                const name =
                  tenant.name ||
                  [tenant.firstName, tenant.lastName]
                    .filter(Boolean)
                    .join(" ") ||
                  tenant.email ||
                  "Unknown";
                const doorLabel = resolveApplicantDoorLabel(tenant, {
                  unitsById,
                  listingsById,
                });
                const isHighlight = highlightTenantId === tenant.id;
                const applicantPhotos = resolveApplicantPhotos(tenant, {
                  listingsById,
                  unitsById,
                });
                const thumb = applicantPhotos[0] || null;
                const entryWhen = formatApplicantEntryWhen(tenant);

                return (
                  <ApplicantListRow
                    key={tenant.id}
                    type="button"
                    $highlight={isHighlight}
                    ref={(el) => {
                      listItemRefs.current[tenant.id] = el;
                    }}
                    onClick={() => setDetailTenantId(tenant.id)}
                    aria-label={`Open details for ${name}`}
                  >
                    <ApplicantListThumb aria-hidden>
                      {thumb ? (
                        <img src={thumb} alt="" />
                      ) : (
                        <ApplicantListThumbPlaceholder>
                          <HomeOutlined />
                        </ApplicantListThumbPlaceholder>
                      )}
                    </ApplicantListThumb>
                    <ApplicantListMain>
                      <TenantName>{name}</TenantName>
                      {doorLabel ? (
                        <TenantDoor>{doorLabel}</TenantDoor>
                      ) : null}
                      {tenant.email ? (
                        <TenantEmail>{tenant.email}</TenantEmail>
                      ) : null}
                      {entryWhen ? (
                        <ApplicantEntryWhen>{entryWhen}</ApplicantEntryWhen>
                      ) : null}
                      {sub ? <StepSubLine $compact>{sub}</StepSubLine> : null}
                    </ApplicantListMain>
                    <ApplicantListMeta>
                      <StageTag color="processing">{step}</StageTag>
                      <ApplicantListProgress>
                        <Progress
                          percent={percent}
                          size="small"
                          status={percent === 100 ? "success" : "active"}
                        />
                      </ApplicantListProgress>
                    </ApplicantListMeta>
                  </ApplicantListRow>
                );
              })}
            </ApplicantListRows>
          ) : (
            <ApplicantList>
              {filteredTenants.map((tenant) => {
                const { step, percent, sub } = getStep(tenant);
                const name =
                  tenant.name ||
                  [tenant.firstName, tenant.lastName]
                    .filter(Boolean)
                    .join(" ") ||
                  tenant.email ||
                  "Unknown";
                const doorLabel = resolveApplicantDoorLabel(tenant, {
                  unitsById,
                  listingsById,
                });
                const isHighlight = highlightTenantId === tenant.id;
                const pipelineIdx = Math.max(0, PIPELINE_ORDER.indexOf(step));
                const applicantPhotos = resolveApplicantPhotos(tenant, {
                  listingsById,
                  unitsById,
                });
                const entryWhen = formatApplicantEntryWhen(tenant);

                return (
                  <TenantApplicantShell
                    key={tenant.id}
                    $highlight={isHighlight}
                    ref={(el) => {
                      listItemRefs.current[tenant.id] = el;
                    }}
                  >
                    <ApplicantCardButton
                      type="button"
                      onClick={() => setDetailTenantId(tenant.id)}
                      aria-label={`Open details for ${name}`}
                    >
                      <ApplicantCardFrame>
                        <ApplicantCardHeader>
                          <ApplicantCardTitle>{name}</ApplicantCardTitle>
                          <ApplicantCardHeaderMeta>
                            <StageTag color="processing">{step}</StageTag>
                            <ApplicantCardStatBox
                              aria-label={`${percent}% complete`}
                            >
                              {percent}%
                            </ApplicantCardStatBox>
                          </ApplicantCardHeaderMeta>
                        </ApplicantCardHeader>

                        <ApplicantCardArt>
                          {applicantPhotos.length > 0 ? (
                            <img src={applicantPhotos[0]} alt="" />
                          ) : (
                            <ApplicantCardArtEmpty>
                              <HomeOutlined />
                            </ApplicantCardArtEmpty>
                          )}
                        </ApplicantCardArt>

                        <ApplicantCardTextBox>
                          <ApplicantCardTypeLine>
                            {doorLabel || "Applicant"}
                          </ApplicantCardTypeLine>
                          <ApplicantCardMetaRow>
                            {tenant.email ? (
                              <TenantEmail>{tenant.email}</TenantEmail>
                            ) : null}
                            {entryWhen ? (
                              <ApplicantEntryWhen>
                                {entryWhen}
                              </ApplicantEntryWhen>
                            ) : null}
                          </ApplicantCardMetaRow>
                          <PipelineMiniTrack
                            aria-label="Applicant pipeline position"
                          >
                            {PIPELINE_STEP_META.map((meta, idx) => {
                              const Icon = meta.Icon;
                              const done = idx < pipelineIdx;
                              const current = idx === pipelineIdx;
                              return (
                                <PipelineMiniStep
                                  key={meta.key}
                                  $done={done}
                                  $current={current}
                                >
                                  <PipelineMiniIconWrap
                                    $done={done}
                                    $current={current}
                                  >
                                    <Icon
                                      style={
                                        done || current
                                          ? {
                                              color: "#ffffff",
                                              filter:
                                                "brightness(0) invert(1)",
                                            }
                                          : undefined
                                      }
                                    />
                                  </PipelineMiniIconWrap>
                                  <PipelineMiniLabel>
                                    {meta.short}
                                  </PipelineMiniLabel>
                                </PipelineMiniStep>
                              );
                            })}
                          </PipelineMiniTrack>
                          {sub ? (
                            <StepSubLine $compact>{sub}</StepSubLine>
                          ) : null}
                          <ExpandPanelCue>
                            <ExpandPanelCueIcon aria-hidden>
                              <EyeOutlined />
                            </ExpandPanelCueIcon>
                            <ExpandPanelCueText>
                              View details &amp; next steps
                            </ExpandPanelCueText>
                            <ExpandPanelCueArrow aria-hidden>
                              <RightOutlined />
                            </ExpandPanelCueArrow>
                          </ExpandPanelCue>
                        </ApplicantCardTextBox>
                      </ApplicantCardFrame>
                    </ApplicantCardButton>
                  </TenantApplicantShell>
                );
              })}
            </ApplicantList>
          )}
        </LeaseUpApplicantsSection>

      <ApplicantDetailModal
        className="lease-up-applicant-detail-modal"
        title={
          detailTenant ? (
            (() => {
              const n =
                detailTenant.name ||
                [detailTenant.firstName, detailTenant.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                detailTenant.email ||
                "Applicant";
              const door = resolveApplicantDoorLabel(detailTenant, {
                unitsById,
                listingsById,
              });
              return (
                <ApplicantDetailHeaderTitle>
                  <ApplicantDetailHeaderName>{n}</ApplicantDetailHeaderName>
                  {door ? (
                    <ApplicantDetailHeaderSub>{door}</ApplicantDetailHeaderSub>
                  ) : null}
                </ApplicantDetailHeaderTitle>
              );
            })()
          ) : (
            "Applicant"
          )
        }
        open={!!detailTenant}
        onCancel={() => setDetailTenantId(null)}
        footer={null}
        width={isMobileLayout ? "calc(100vw - 24px)" : 720}
        centered
        destroyOnClose
        styles={{
          content: { padding: 0 },
          header: { margin: 0 },
          body: {
            maxHeight: isMobileLayout
              ? "calc(100dvh - 120px)"
              : "calc(100dvh - 140px)",
            overflowY: "auto",
            padding: 0,
          },
        }}
      >
        {detailTenant &&
          (() => {
            const tenant = detailTenant;
            const { step } = getStep(tenant);
            const actionState = getApplicantActionState(tenant);
            const name =
              tenant.name ||
              [tenant.firstName, tenant.lastName].filter(Boolean).join(" ") ||
              tenant.email ||
              "Unknown";
            const doorLabel = resolveApplicantDoorLabel(tenant, {
              unitsById,
              listingsById,
            });
            const activityRows = buildLeaseUpActivityRows(tenant);
            const applicationDetailRows = getApplicationDetailRows(tenant);
            const applicantGallery = resolveApplicantGallery(tenant, {
              listingsById,
              unitsById,
            });

            return (
              <ApplicantDetailBody className="lease-up-container">
                {applicantGallery.length > 0 ? (
                  <ApplicantPhotoCarousel
                    slides={applicantGallery}
                    label={doorLabel || name}
                    photoKey={`detail-${tenant.id}`}
                    hero
                  />
                ) : (
                  <ApplicantDetailNoPhoto>
                    <HomeOutlined />
                    <span>No application documents or listing photos yet</span>
                  </ApplicantDetailNoPhoto>
                )}

                <ApplicantDetailContent>
                  <ApplicantDetailStageRow>
                    <StageTag color="processing">{step}</StageTag>
                    {tenant.email && (
                      <Typography.Text type="secondary">
                        {tenant.email}
                      </Typography.Text>
                    )}
                  </ApplicantDetailStageRow>

                  <TenantActionsBand>
                    <NextStepLabel>Next step</NextStepLabel>
                    <TenantActionsArea>
                      {actionState.kind === "prospect" && (
                        <Tooltip title="Copies the full rental application URL (/apply/…) so they can submit the complete application, not just tour or contact info.">
                          <LeaseUpTooltipTrigger>
                            <Button
                              type="primary"
                              size="large"
                              icon={<LinkOutlined />}
                              block
                              onClick={() => {
                                const url = `${window.location.origin}/apply/${
                                  tenant.listingId || ""
                                }`;
                                navigator.clipboard?.writeText(url).then(() => {
                                  trackProduct("application_link_copied", {
                                    tenant_id: tenant.id,
                                    listing_id: tenant.listingId || null,
                                    source: "lease_ups",
                                  });
                                  message.success(
                                    "Application link copied to clipboard"
                                  );
                                });
                              }}
                              disabled={!tenant.listingId}
                            >
                              Copy application link
                            </Button>
                          </LeaseUpTooltipTrigger>
                        </Tooltip>
                      )}

                      {actionState.kind === "invite_screen" && (
                        <ActionStack>
                          <Alert
                            type="info"
                            showIcon
                            message="Applicant-paid background check"
                            description="Email them a pay link ($55). After they pay, Checkr starts automatically — LandlordOps does not front the screening cost."
                            className="lease-up-muted-alert"
                          />
                          <Button
                            type="primary"
                            size="large"
                            icon={
                              <UserAddOutlined
                                style={{
                                  filter: "brightness(0) invert(1)",
                                }}
                              />
                            }
                            block
                            loading={loadingScreening === tenant.id}
                            onClick={() => handleInviteToScreen(tenant)}
                          >
                            Email screening invite
                          </Button>
                        </ActionStack>
                      )}

                      {(actionState.kind === "screening_starting" ||
                        actionState.kind === "screening_pending") && (
                        <ActionStack>
                          <Alert
                            type="info"
                            showIcon
                            message={
                              actionState.kind === "screening_starting"
                                ? "Fee paid — starting screening"
                                : "Screening in progress"
                            }
                            description={
                              actionState.kind === "screening_starting"
                                ? "Payment received. The Checkr invite is being sent (or was just sent)."
                                : "Waiting for the applicant to finish screening. You can still send a lease when ready."
                            }
                            className="lease-up-muted-alert"
                          />
                          {tenant.screening_invite_link ? (
                            <Button
                              type="default"
                              size="large"
                              block
                              onClick={() => {
                                navigator.clipboard
                                  ?.writeText(tenant.screening_invite_link)
                                  .then(() =>
                                    message.success("Screening link copied")
                                  );
                              }}
                            >
                              Copy screening link
                            </Button>
                          ) : (
                            <Button
                              type="default"
                              size="large"
                              block
                              loading={loadingScreening === tenant.id}
                              onClick={() => handleInviteToScreen(tenant)}
                            >
                              Resend screening invite
                            </Button>
                          )}
                          <Button
                            type="primary"
                            size="large"
                            icon={
                              <SendOutlined
                                style={{
                                  filter: "brightness(0) invert(1)",
                                }}
                              />
                            }
                            block
                            onClick={() => handleSendLease(tenant)}
                          >
                            Send lease
                          </Button>
                        </ActionStack>
                      )}

                      {actionState.kind === "screening_failed" && (
                        <ActionStack>
                          <Alert
                            type="warning"
                            showIcon
                            message="Screening needs review"
                            description="The report came back with issues. Review it before sending a lease, or proceed if you accept the risk."
                            className="lease-up-muted-alert"
                          />
                          {tenant.screening_report_url ? (
                            <Button
                              type="default"
                              size="large"
                              block
                              href={tenant.screening_report_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open screening report
                            </Button>
                          ) : null}
                          <Button
                            type="primary"
                            size="large"
                            icon={
                              <SendOutlined
                                style={{
                                  filter: "brightness(0) invert(1)",
                                }}
                              />
                            }
                            block
                            onClick={() => handleSendLease(tenant)}
                          >
                            Send lease anyway
                          </Button>
                        </ActionStack>
                      )}

                      {actionState.kind === "ready_lease" && (
                        <ActionStack>
                          {tenant.screening_status === "passed" ? (
                            <Alert
                              type="success"
                              showIcon
                              message="Screening complete"
                              description="You can send a lease when ready."
                              className="lease-up-muted-alert"
                            />
                          ) : (
                            <Alert
                              type="info"
                              showIcon
                              message="Ready for lease"
                              description="Send a lease when you are ready to proceed."
                              className="lease-up-muted-alert"
                            />
                          )}
                          {tenant.screening_report_url ? (
                            <Button
                              type="default"
                              size="large"
                              block
                              href={tenant.screening_report_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open screening report
                            </Button>
                          ) : null}
                          <Alert
                            type="warning"
                            showIcon
                            message="Lease send coming soon"
                            description="Uploading a lease and sending it for e-signature will be available here shortly. For now, mark as signed after you complete signing outside LandlordOps."
                            className="lease-up-coming-soon-alert"
                          />
                          <Button
                            type="primary"
                            size="large"
                            icon={
                              <CheckCircleOutlined
                                style={{
                                  filter: "brightness(0) invert(1)",
                                }}
                              />
                            }
                            block
                            onClick={() => handleMarkLeaseSigned(tenant)}
                          >
                            Mark as signed
                          </Button>
                          <Tooltip title="Lease send and e-signature are coming soon.">
                            <LeaseUpTooltipTrigger>
                              <Button
                                type="default"
                                size="large"
                                icon={<SendOutlined />}
                                block
                                disabled
                              >
                                Send lease
                              </Button>
                            </LeaseUpTooltipTrigger>
                          </Tooltip>
                        </ActionStack>
                      )}

                      {actionState.kind === "lease_out" && (
                        <ActionStack>
                          <LeaseStatusRow>
                            <Tag color="processing">Awaiting signature</Tag>
                            <Typography.Text type="secondary">
                              The lease is with the applicant for signing, or
                              mark signed if they already signed outside
                              LandlordOps.
                            </Typography.Text>
                          </LeaseStatusRow>
                          <Button
                            type="primary"
                            size="large"
                            icon={
                              <CheckCircleOutlined
                                style={{
                                  filter: "brightness(0) invert(1)",
                                }}
                              />
                            }
                            block
                            onClick={() => handleMarkLeaseSigned(tenant)}
                          >
                            Mark as signed
                          </Button>
                        </ActionStack>
                      )}

                      {actionState.kind === "done" && (
                        <LeaseStatusRow>
                          <Tag color="success">Fully onboarded</Tag>
                          {tenant.docuSealAuditLogUrl && (
                            <a
                              href={tenant.docuSealAuditLogUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View signed document
                            </a>
                          )}
                        </LeaseStatusRow>
                      )}
                    </TenantActionsArea>
                  </TenantActionsBand>

                  {activityRows.length > 0 && (
                    <ActivityLogSection>
                      <ActivityLogTitle>Activity</ActivityLogTitle>
                      <ActivityLogList>
                        {activityRows.map((row) => (
                          <ActivityLogItem key={row.key}>
                            <ActivityLogLabel>{row.label}</ActivityLogLabel>
                            <ActivityLogDate>{row.at}</ActivityLogDate>
                          </ActivityLogItem>
                        ))}
                      </ActivityLogList>
                    </ActivityLogSection>
                  )}

                  {applicationDetailRows.length > 0 && (
                    <TenantDocumentsBand>
                      <DocUploadSection>
                        <DocUploadTitle>Application details</DocUploadTitle>
                        <DocUploadHint>
                          Submitted on the public apply form.
                        </DocUploadHint>
                        <ApplicationDetailsGrid>
                          {applicationDetailRows.map((row) => (
                            <ApplicationDetailItem key={row.label}>
                              <ApplicationDetailLabel>
                                {row.label}
                              </ApplicationDetailLabel>
                              <ApplicationDetailValue>
                                {row.value}
                              </ApplicationDetailValue>
                            </ApplicationDetailItem>
                          ))}
                        </ApplicationDetailsGrid>
                      </DocUploadSection>
                    </TenantDocumentsBand>
                  )}

                  {tenant.ingestion_status !== "prospect" && (
                    <TenantDocumentsBand>
                      <DocUploadSection>
                        <DocUploadTitle>
                          Application documents for {name}
                        </DocUploadTitle>
                        <DocUploadHint>
                          Upload as many files as you need for each type (ID,
                          paystubs, W-2).
                        </DocUploadHint>
                        {["photo_id", "paystub", "w2"].map((docType) => {
                          const key = `${tenant.id}-${docType}`;
                          const progress = uploadProgress[key];
                          const docsOfType = (
                            tenant.uploadedDocuments || []
                          ).filter((d) => d.type === docType);
                          const hasDoc = docsOfType.length > 0;
                          return (
                            <DocRow
                              key={docType}
                              className="lease-up-doc-row"
                            >
                              <DocRowMain>
                                <DocRowLabel>
                                  {DOC_LABELS[docType]}
                                  {hasDoc ? ` (${docsOfType.length})` : ""}
                                </DocRowLabel>
                                {hasDoc ? (
                                  <DocLinks>
                                    {docsOfType.map((d, i) => {
                                      const removeKey = `remove-${tenant.id}-${d.url}`;
                                      const removing =
                                        typeof uploadProgress[removeKey] ===
                                        "number";
                                      const label =
                                        d.name ||
                                        `${DOC_LABELS[docType]} ${i + 1}`;
                                      const isImage = isLikelyImageDoc(d);
                                      return (
                                        <DocFileChip
                                          key={`${docType}-${i}-${d.url}`}
                                        >
                                          <DocFilePreview
                                            href={d.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={label}
                                            $image={isImage}
                                          >
                                            {isImage ? (
                                              <img src={d.url} alt="" />
                                            ) : (
                                              <FileTextOutlined />
                                            )}
                                          </DocFilePreview>
                                          <DocFileName
                                            href={d.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={label}
                                          >
                                            {label}
                                          </DocFileName>
                                          <DocFileRemoveBtn
                                            type="text"
                                            size="small"
                                            danger
                                            aria-label={`Remove ${label}`}
                                            icon={<DeleteOutlined />}
                                            loading={removing}
                                            disabled={removing}
                                            onClick={() =>
                                              confirmDocumentRemove(
                                                tenant,
                                                d,
                                                label
                                              )
                                            }
                                          />
                                        </DocFileChip>
                                      );
                                    })}
                                  </DocLinks>
                                ) : (
                                  <DocMissing>No files yet</DocMissing>
                                )}
                              </DocRowMain>
                              <DocRowUploadCell>
                                <input
                                  type="file"
                                  accept="image/*,.pdf"
                                  multiple
                                  ref={(el) => {
                                    fileInputRefs.current[key] = el;
                                  }}
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const selected = Array.from(
                                      e.target?.files || []
                                    );
                                    if (selected.length)
                                      handleDocumentUpload(
                                        tenant,
                                        docType,
                                        selected
                                      );
                                    e.target.value = "";
                                  }}
                                />
                                <Button
                                  type="primary"
                                  className="lease-up-doc-upload-btn"
                                  icon={
                                    <UploadOutlined
                                      style={{
                                        filter: "brightness(0) invert(1)",
                                      }}
                                    />
                                  }
                                  onClick={() =>
                                    fileInputRefs.current[key]?.click()
                                  }
                                  loading={typeof progress === "number"}
                                  disabled={typeof progress === "number"}
                                >
                                  {typeof progress === "number"
                                    ? "Uploading"
                                    : hasDoc
                                    ? "Add files"
                                    : "Upload files"}
                                </Button>
                              </DocRowUploadCell>
                            </DocRow>
                          );
                        })}
                      </DocUploadSection>
                    </TenantDocumentsBand>
                  )}

                  <ApplicantDetailDangerZone>
                    <Button
                      danger
                      size="large"
                      icon={<DeleteOutlined />}
                      block
                      onClick={() => handleDeleteLeaseUp(tenant)}
                    >
                      Delete lease-up
                    </Button>
                  </ApplicantDetailDangerZone>
                </ApplicantDetailContent>
              </ApplicantDetailBody>
            );
          })()}
      </ApplicantDetailModal>

      <OnboardStyledModal
        title="Send lease"
        open={onboardModalOpen}
        onCancel={() => {
          if (
            onboardStatus.step3 === "process" ||
            onboardStatus.step1 === "process"
          )
            return;
          setOnboardModalOpen(false);
          setOnboardTenant(null);
          setOnboardLeaseFile(null);
        }}
        closable={
          onboardStatus.step3 !== "process" && onboardStatus.step1 !== "process"
        }
        footer={
          onboardTenant &&
          onboardStatus.step1 === "pending" &&
          onboardStatus.step3 !== "process" ? (
            <Button
              type="primary"
              size="large"
              icon={
                <SendOutlined
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              }
              onClick={handleStartOnboarding}
              disabled={!onboardLegalAck || !onboardLeaseFile}
              block={isMobileLayout}
            >
              Send lease
            </Button>
          ) : null
        }
        width={isMobileLayout ? "calc(100vw - 24px)" : 640}
        centered
        styles={{
          body: {
            maxHeight: isMobileLayout
              ? "calc(100dvh - 160px)"
              : "calc(100dvh - 180px)",
            overflowY: "auto",
          },
        }}
        maskClosable={false}
        destroyOnClose
      >
        {onboardTenant && (
          <OnboardBody>
            <OnboardIntro>
              Send a lease to{" "}
              <strong>{onboardTenant.name || onboardTenant.email}</strong> for
              e-signature. Upload your PDF. We don&apos;t provide templates.
            </OnboardIntro>

            {onboardStatus.step1 === "pending" && (
              <OnboardUploadBlock>
                <OnboardUploadLabel>Your lease (PDF)</OnboardUploadLabel>
                {onboardLeaseFile ? (
                  <OnboardLeaseReady>
                    <OnboardLeaseReadyIcon aria-hidden>
                      <CheckCircleOutlined />
                    </OnboardLeaseReadyIcon>
                    <OnboardLeaseReadyMeta>
                      <OnboardLeaseReadyTitle>PDF ready</OnboardLeaseReadyTitle>
                      <OnboardLeaseReadyName title={onboardLeaseFile.name}>
                        {onboardLeaseFile.name}
                      </OnboardLeaseReadyName>
                    </OnboardLeaseReadyMeta>
                    <OnboardLeaseRemoveBtn
                      type="text"
                      danger
                      aria-label="Remove lease PDF"
                      icon={<DeleteOutlined />}
                      onClick={() => setOnboardLeaseFile(null)}
                    />
                  </OnboardLeaseReady>
                ) : (
                  <Upload.Dragger
                    className="lease-onboard-uploader"
                    accept=".pdf,application/pdf"
                    multiple={false}
                    maxCount={1}
                    showUploadList={false}
                    beforeUpload={(file) => {
                      const isPdf =
                        file.type === "application/pdf" ||
                        /\.pdf$/i.test(file.name || "");
                      if (!isPdf) {
                        message.error("Please upload a PDF lease document.");
                        return Upload.LIST_IGNORE;
                      }
                      if (file.size > 15 * 1024 * 1024) {
                        message.error("Lease PDF must be 15MB or smaller.");
                        return Upload.LIST_IGNORE;
                      }
                      setOnboardLeaseFile(file);
                      return false;
                    }}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Drop PDF here, or click to browse
                    </p>
                    <p className="ant-upload-hint">Max 15MB</p>
                  </Upload.Dragger>
                )}

                <OnboardAckWrap>
                  <Checkbox
                    checked={onboardLegalAck}
                    onChange={(e) => setOnboardLegalAck(e.target.checked)}
                  >
                    I confirm this is my lease and that it meets my legal
                    requirements. LandlordOps does not provide templates or
                    legal advice.
                  </Checkbox>
                </OnboardAckWrap>
              </OnboardUploadBlock>
            )}

            <OnboardStepsWrap>
              <OnboardStepsLabel>What happens next</OnboardStepsLabel>
              <OnboardStepList>
                <OnboardStepItem
                  $active={
                    onboardStatus.step1 === "process" ||
                    onboardStatus.step1 === "finish"
                  }
                  $done={onboardStatus.step1 === "finish"}
                  $error={onboardStatus.step1 === "error"}
                >
                  <OnboardStepBadge>
                    {onboardStatus.step1 === "finish" ? (
                      <CheckCircleOutlined />
                    ) : (
                      "1"
                    )}
                  </OnboardStepBadge>
                  <OnboardStepCopy>
                    <OnboardStepTitle>Send lease</OnboardStepTitle>
                    <OnboardStepDesc>Your PDF via DocuSeal</OnboardStepDesc>
                  </OnboardStepCopy>
                </OnboardStepItem>
                <OnboardStepItem
                  $active={onboardStatus.step2 === "finish"}
                  $done={onboardStatus.step2 === "finish"}
                >
                  <OnboardStepBadge>
                    {onboardStatus.step2 === "finish" ? (
                      <CheckCircleOutlined />
                    ) : (
                      "2"
                    )}
                  </OnboardStepBadge>
                  <OnboardStepCopy>
                    <OnboardStepTitle>Welcome email</OnboardStepTitle>
                    <OnboardStepDesc>Signing link for the applicant</OnboardStepDesc>
                  </OnboardStepCopy>
                </OnboardStepItem>
              </OnboardStepList>
            </OnboardStepsWrap>
          </OnboardBody>
        )}
      </OnboardStyledModal>
    </OnboardingWrap>
  );
};

export default OnboardingTab;

const OnboardingWrap = styled.div`
  padding: 0;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  @media ${device.mobile} {
    padding: 0;
  }
`;

const ProgressLegendSection = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProgressLegendLabel = styled.div`
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ProgressLegend = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
  @media ${device.mobile} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem 1rem;
    max-width: 280px;
  }
`;

const PipelineEmptyShell = styled.div`
  width: 100%;
  padding: clamp(0.75rem, 2.5vw, 1.5rem) 0 clamp(0.5rem, 2vw, 1rem);
  box-sizing: border-box;
`;

const PipelineEmptyCard = styled.div`
  max-width: 34rem;
  margin: 0 auto;
  text-align: center;
  padding: clamp(1.25rem, 4vw, 2rem) clamp(1rem, 3vw, 1.75rem);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px dashed rgba(82, 196, 26, 0.42);
  box-shadow: 0 4px 20px rgba(35, 120, 4, 0.06);
  box-sizing: border-box;
`;

const PipelineEmptyIconWrap = styled.div`
  width: 4.5rem;
  height: 4.5rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.65rem;
  color: var(--color-primary, #2c5f2d);
  background: linear-gradient(
    145deg,
    rgba(246, 255, 237, 1) 0%,
    rgba(217, 247, 190, 0.65) 100%
  );
  border: 1px solid rgba(82, 196, 26, 0.28);
`;

const PipelineEmptyTitle = styled.h3`
  margin: 0 0 0.65rem;
  font-size: clamp(1.1rem, 3.2vw, 1.35rem);
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  line-height: 1.25;
  font-family: var(--font-primary, inherit);
`;

const PipelineEmptyLead = styled.p`
  margin: 0 0 1.25rem;
  font-size: clamp(0.875rem, 2.4vw, 0.95rem);
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.62);
  text-align: left;
  font-family: var(--font-secondary, inherit);
`;

const PipelineEmptyStepList = styled.ol`
  counter-reset: pipeline-empty-step;
  list-style: none;
  margin: 0 auto 1.5rem;
  padding: 0;
  max-width: 28rem;
  text-align: left;
`;

const PipelineEmptyStep = styled.li`
  counter-increment: pipeline-empty-step;
  position: relative;
  padding-left: 2.35rem;
  margin-bottom: 0.8rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.78);

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: counter(pipeline-empty-step);
    position: absolute;
    left: 0;
    top: 0.1rem;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background: var(--color-primary, #2c5f2d);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
`;

const PipelineEmptyActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  max-width: 20rem;
  margin: 0 auto;

  @media ${device.minTablet} {
    align-items: center;
  }
`;

const PipelineFilterEmpty = styled.div`
  text-align: center;
  padding: clamp(1.25rem, 4vw, 2rem) clamp(0.75rem, 3vw, 1.25rem);
  margin: 0.25rem 0 0.5rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.65);
  border: 1px dashed rgba(82, 196, 26, 0.35);
  max-width: 26rem;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
`;

const PipelineFilterEmptyIcon = styled.div`
  width: 2.75rem;
  height: 2.75rem;
  margin: 0 auto 0.65rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: var(--color-primary, #2c5f2d);
  background: rgba(246, 255, 237, 0.95);
  border: 1px solid rgba(82, 196, 26, 0.22);
`;

const PipelineFilterEmptyTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 0.4rem;
`;

const PipelineFilterEmptyText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.6);
`;

const GetStartedButton = styled(Button)`
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 46px !important;
  width: 100%;
  font-weight: 600 !important;
  box-shadow: 0 4px 14px rgba(44, 95, 45, 0.2);

  .ant-btn-icon {
    margin-inline-end: 8px;
    display: inline-flex;
    align-items: center;
  }

  @media ${device.minTablet} {
    width: auto;
    min-width: 220px;
    padding-left: 1.75rem !important;
    padding-right: 1.75rem !important;
  }
`;

/** Block-level wrapper so Tooltip + disabled / block buttons layout like the rest of the app */
const LeaseUpTooltipTrigger = styled.span`
  display: block;
  width: 100%;
`;

const LegendItem = styled.span`
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  gap: 0.35rem;
  @media ${device.mobile} {
    justify-content: center;
  }
`;

const TenantRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem 1.25rem;
  width: 100%;
`;

/** Fills space between tenant details and action buttons so the row uses full card width */
const TenantInfoColumn = styled.div`
  flex: 1 1 200px;
  min-width: 0;
`;

const TenantProgressWrap = styled.div`
  width: 100%;
  max-width: 100%;
  margin-top: 4px;
`;

const TenantName = styled.div`
  font-weight: 600;
  color: var(--color-text);
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const TenantDoor = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-light, #64748b);
  overflow-wrap: anywhere;
  word-break: break-word;
  margin-top: 2px;
`;

const ApplicantEntryWhen = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.35;
`;

const TenantEmail = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-muted);
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const StepSub = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin-top: 4px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: auto;
`;

const DocUploadSection = styled.div`
  flex: 1 1 100%;
  margin-top: 0;
  padding-top: 0;
  border-top: none;
  width: 100%;
  min-width: 0;
  text-align: left;
`;

const DocUploadTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  text-align: left;
  line-height: 1.4;
`;

const DocUploadHint = styled.p`
  margin: 0 0 12px;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #6b7280;
`;

const ApplicationDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  padding: 12px 0 0;
  background: transparent;
  border: none;
  border-radius: 0;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const ApplicationDetailItem = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ApplicationDetailLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.35;
`;

const ApplicationDetailValue = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.84);
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const DocRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 0;
  font-size: 0.875rem;
  flex-wrap: wrap;
  padding: 12px 0;
  background: transparent;
  border: none;
  border-bottom: 1px solid #eef2f7;
  border-radius: 0;
  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
    padding-bottom: 0;
  }
  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const DocRowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const DocRowLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
  min-width: 0;
`;

const DocLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.45rem;
  min-width: 0;
`;

const DocFileChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  max-width: 100%;
  padding: 4px 6px 4px 4px;
  border-radius: 8px;
  background: #f1f5f9;
  border: none;
`;

const DocFilePreview = styled.a`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$image ? "#e2e8f0" : "#e2e8f0")};
  border: none;
  text-decoration: none;
  color: #64748b;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .anticon {
    font-size: 16px;
  }
`;

const DocFileName = styled.a`
  flex: 0 1 auto;
  min-width: 0;
  max-width: 220px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-primary, #2c5f2d);
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
`;

const DocFileRemoveBtn = styled(Button)`
  && {
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    padding: 0;
    color: #111827 !important;
  }

  && .anticon,
  && .anticon svg {
    color: #111827 !important;
    fill: #111827 !important;
    filter: none !important;
  }

  &&:hover {
    color: #dc2626 !important;
    background: #fef2f2 !important;
  }

  &&:hover .anticon,
  &&:hover .anticon svg {
    color: #dc2626 !important;
    fill: #dc2626 !important;
  }
`;

const DocMissing = styled.span`
  font-size: 0.8125rem;
  line-height: 1.4;
  color: #6b7280;
`;

const DocRowUploadCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  .lease-up-doc-upload-btn.ant-btn {
    gap: 6px !important;
    font-size: 0.8125rem !important;
  }

  .lease-up-doc-upload-btn.ant-btn .ant-btn-icon {
    margin-inline-end: 0 !important;
  }

  .lease-up-doc-upload-btn.ant-btn-primary .anticon {
    color: #ffffff !important;
  }

  @media ${device.mobile} {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`;

const LeaseUpStatsSection = styled.div`
  margin-bottom: 1.25rem;
  width: 100%;
  min-width: 0;

  .ant-card {
    --stat-title-size: 0.8125rem;
    --stat-value-size: 1.375rem;
    --stat-suffix-size: 1.375rem;
    --stat-icon-size: 1.25rem;
    height: 100%;
  }

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-size: var(--stat-title-size) !important;
    line-height: 1.3;
    font-weight: 600;
    color: var(--color-text-light);
    margin-bottom: 0.4rem;
  }

  .ant-statistic-content {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-value,
  .ant-statistic-content-value-int,
  .ant-statistic-content-value-decimal {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700;
    color: var(--color-text) !important;
  }

  .ant-statistic-content-prefix .anticon {
    font-size: var(--stat-icon-size) !important;
  }

  .stats-card.ant-card .ant-card-body {
    padding: 1.15rem 0.85rem !important;
  }

  @media ${device.mobile} {
    margin-bottom: 1rem;
  }
`;

const LeaseUpApplicantsSection = styled.div`
  min-width: 0;
`;

const PropertyFilterRow = styled.div`
  margin-bottom: 14px;
  min-width: 0;
`;

const LeaseUpToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  width: 100%;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
  }
`;

const ViewToggleWrap = styled.div`
  display: none;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem;
  background: #f1f5f9;
  border-radius: 10px;
  border: 1px solid #e2e8f0;

  @media (min-width: 901px) {
    display: inline-flex;
  }
`;

const ViewToggleButton = styled(Button)`
  min-width: 36px;
  height: 36px;
  padding: 0;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
`;

/** Door left, sort right on desktop. */
const PropertyFilterInner = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$align",
})`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: ${(p) =>
    p.$align === "end" ? "flex-end" : "flex-start"};
  gap: 10px 12px;
  width: fit-content;
  max-width: 100%;
  margin-left: ${(p) => (p.$align === "end" ? "auto" : "0")};

  @media ${device.mobile} {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    max-width: none;
    margin-left: 0;
  }
`;

const PropertyFilterSelect = styled(Select)`
  width: 11.5rem !important;
  min-width: 10rem;
  max-width: 100%;
  font-family: var(--font-secondary) !important;
  font-size: 0.875rem !important;

  .ant-select-selector {
    min-height: 42px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;
    outline: none !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.12) !important;
    outline: none !important;
  }

  .ant-select-selection-wrap {
    width: 100%;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder,
  .ant-select-selection-search-input {
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;
    outline: none !important;
    box-shadow: none !important;
    text-align: left !important;
  }

  .ant-select-selection-item {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    text-align: left !important;
  }

  @media ${device.mobile} {
    width: 100% !important;
    min-width: 0;
    flex: none;
  }
`;

const DoorFilterSelect = styled(Select)`
  width: 28rem !important;
  min-width: 18rem;
  max-width: 100%;
  font-family: var(--font-secondary) !important;
  font-size: 0.875rem !important;

  .ant-select-selector {
    min-height: 42px !important;
    padding-block: 4px !important;
    padding-inline: 11px 32px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &.ant-select-focused .ant-select-selector,
  &.ant-select-open .ant-select-selector {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 2px rgba(44, 95, 45, 0.12) !important;
    outline: none !important;
  }

  .ant-select-selection-wrap {
    width: 100%;
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
  }

  .ant-select-selection-item {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    inset-inline-start: 11px !important;
    inset-inline-end: 32px !important;
    text-align: left !important;
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
    background: transparent !important;
  }

  .ant-select-selection-placeholder {
    inset-inline-start: 11px !important;
    inset-inline-end: 32px !important;
    text-align: left !important;
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;
  }

  .ant-select-selection-search {
    inset-inline-start: 11px !important;
    inset-inline-end: 32px !important;
  }

  .ant-select-selection-search-input {
    text-align: left !important;
    font-family: var(--font-secondary) !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    line-height: 1.4 !important;
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
    background: transparent !important;
  }

  .ant-select-item-option-content {
    white-space: normal;
    font-size: 0.875rem !important;
  }

  @media ${device.mobile} {
    width: 100% !important;
    min-width: 0;
  }
`;

const DoorOptionRow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
  vertical-align: middle;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: var(--font-secondary);
  line-height: 1.4;
`;

const DoorOptionThumb = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  overflow: hidden;
  flex-shrink: 0;
  background: #eef2f0;
  border: 1px solid #e5e7eb;
  display: inline-flex;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const DoorOptionPlaceholder = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%);
  border: 1px solid #e2e8f0;
  color: #94a3b8;
  font-size: 14px;

  .anticon {
    color: #94a3b8 !important;
  }
`;

const DoorOptionText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: var(--font-secondary);
  line-height: 1.4;
`;

const PropertyFilterLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-secondary);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.72);
  margin: 0;
  flex-shrink: 0;
  .anticon {
    font-size: 0.875rem;
    color: var(--color-primary, #2c5f2d);
  }

  @media ${device.mobile} {
    justify-content: flex-start;
  }
`;

const ActivityLogSection = styled.div`
  margin: 12px 0 4px;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(240, 249, 255, 0.9);
  border: 1px solid #bae0ff;
  min-width: 0;
`;

const ActivityLogTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 10px;
`;

const ActivityLogList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActivityLogItem = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px 12px;
  font-size: 0.8125rem;
  line-height: 1.45;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.08);
  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const ActivityLogLabel = styled.span`
  color: rgba(0, 0, 0, 0.82);
  font-weight: 500;
  min-width: 0;
`;

const ActivityLogDate = styled.span`
  color: rgba(0, 0, 0, 0.5);
  font-size: 0.75rem;
  white-space: nowrap;
  flex-shrink: 0;
`;

const SectionEyebrow = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$variant",
})`
  font-size: ${({ $variant }) =>
    $variant === "pipeline" ? "0.875rem" : "0.75rem"};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: ${({ $variant }) =>
    $variant === "pipeline" ? "0.04em" : "0.08em"};
  margin-bottom: ${({ $variant }) =>
    $variant === "pipeline" ? "12px" : "10px"};
  color: ${({ $variant }) =>
    $variant === "pipeline" ? "rgba(0, 0, 0, 0.72)" : "#4b5563"};
  line-height: 1.4;
`;

const ApplicantList = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.75rem;
  min-width: 0;
  justify-items: center;

  @media (min-width: 1100px) {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 2rem;
  }

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ApplicantListRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;
`;

const ApplicantListRow = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== "$highlight",
})`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  width: 100%;
  margin: 0;
  padding: 0.75rem 1rem;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;
  background: #ffffff;
  border: 2px solid #64748b;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  box-sizing: border-box;

  ${(p) =>
    p.$highlight
      ? `
    border-color: #1677ff;
    box-shadow:
      0 0 0 3px rgba(22, 119, 255, 0.22),
      0 6px 22px rgba(22, 119, 255, 0.14);
  `
      : ""}

  &:focus-visible {
    outline: 2px solid var(--color-primary, #2c5f2d);
    outline-offset: 2px;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 72px minmax(0, 1fr) auto;
  }
`;

const ApplicantListThumb = styled.div`
  width: 88px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  background: #eef2f7;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 1100px) {
    width: 72px;
    height: 64px;
  }
`;

const ApplicantListThumbPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 1.25rem;
  background: #f8fafc;
`;

const ApplicantListMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ApplicantListMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 6px;
  width: 148px;
  flex-shrink: 0;
  min-width: 0;
`;

const ApplicantListProgress = styled.div`
  width: 100%;

  .ant-progress-text {
    font-size: 12px !important;
  }
`;

const TenantApplicantShell = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$highlight",
})`
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
  box-sizing: border-box;
  text-align: left;
  background: linear-gradient(165deg, #f4f7f4 0%, #e8efe9 100%);
  border: 2px solid #2c5f2d;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.1);
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: #234a24;
    box-shadow: 0 8px 22px rgba(44, 95, 45, 0.14);
    transform: translateY(-2px);
  }

  @media ${device.mobile} {
    max-width: 100%;
  }

  ${(p) =>
    p.$highlight
      ? `
    border-color: #1677ff;
    box-shadow:
      0 0 0 2px rgba(22, 119, 255, 0.28),
      0 8px 22px rgba(22, 119, 255, 0.14);
  `
      : ""}
`;

const ApplicantCardButton = styled.button`
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;

  &:focus-visible {
    outline: 2px solid var(--color-primary, #2c5f2d);
    outline-offset: 2px;
  }
`;

const ApplicantCardFrame = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  min-width: 0;

  @media ${device.mobile} {
    gap: 10px;
    padding: 12px;
  }
`;

const ApplicantCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 2px 0 0;
  min-width: 0;
  flex-wrap: wrap;
`;

const ApplicantCardHeaderMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;

  .ant-tag {
    margin: 0 !important;
    line-height: 1.25;
    padding: 2px 6px;
  }
`;

const ApplicantCardTitle = styled.div`
  font-family: var(--font-primary, inherit);
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.35;
  color: var(--color-text, #111827);
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  flex: 1 1 auto;
  padding-top: 2px;
`;

const ApplicantCardArt = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  height: auto;
  overflow: hidden;
  border-radius: 0;
  border: none;
  background: #eef2f7;
  box-shadow: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 0;
  }
`;

const ApplicantCardArtEmpty = styled.div`
  width: 100%;
  height: 100%;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 1.75rem;
  background: #f8fafc;
`;

const ApplicantCardTypeLine = styled.div`
  padding: 0 0 8px;
  margin: 0 0 8px;
  border-bottom: 1px solid #e4ebe5;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.4;
  color: #334155;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const ApplicantCardMetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ApplicantCardTextBox = styled.div`
  padding: 4px 2px 0;
  border-radius: 0;
  background: transparent;
  border: none;
  min-width: 0;

  @media ${device.mobile} {
    padding: 2px 0 0;
  }
`;

const ApplicantCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eef2f0;
  min-width: 0;
`;

const ApplicantCardFooterMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  .ant-tag {
    margin: 0 !important;
    line-height: 1.25;
    padding: 2px 6px;
  }
`;

const ApplicantCardStatBox = styled.div`
  padding: 3px 7px;
  border-radius: 6px;
  background: #eef5ee;
  border: 1.5px solid #2c5f2d;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1.2;
  color: #1f3d20;
  text-align: center;
  white-space: nowrap;
`;

/* Kept for list view / other surfaces */
const ApplicantCardCover = styled.div`
  position: relative;
  width: 100%;
  height: 132px;
  overflow: hidden;
  background: #eef2f7;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ApplicantCardBody = styled.div`
  padding: 14px 16px 16px;
`;

const TenantCollapsedPipeline = styled.div`
  width: 100%;
  margin-top: 10px;
  padding: 12px 14px 12px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #adc6ff;
  border-radius: 10px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  min-width: 0;
`;

const ApplicantDetailModal = styled(Modal)`
  --lo-modal-header-height: 56px;
  --lo-modal-close-size: 32px;

  &.ant-modal {
    padding-bottom: 0;
    max-width: calc(100vw - 24px);
  }

  .ant-modal-content {
    border-radius: 14px;
    overflow: hidden;
    padding: 0 !important;
    margin: 0 !important;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100%;
    max-height: calc(100dvh - 32px);
    display: flex;
    flex-direction: column;
  }

  .ant-modal-header {
    flex-shrink: 0;
    background: var(--color-primary, #2c5f2d) !important;
    border-bottom: none;
    border-radius: 14px 14px 0 0;
    padding: 12px 52px 12px 20px !important;
    margin: 0 !important;
    min-height: var(--lo-modal-header-height);
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }

  .ant-modal-title {
    color: #ffffff !important;
    font-weight: 700;
    font-size: 1.05rem;
    line-height: 1.25;
    width: 100%;
  }

  .ant-modal-close {
    color: #ffffff !important;
    top: 12px !important;
    right: 16px !important;
    width: var(--lo-modal-close-size) !important;
    height: var(--lo-modal-close-size) !important;
    padding: 0 !important;
    margin: 0 !important;
    transform: none !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;

    .anticon,
    .anticon svg,
    .ant-modal-close-x,
    .ant-modal-close-icon,
    svg {
      color: #ffffff !important;
      fill: #ffffff !important;
    }

    &:hover {
      color: #ffffff !important;
      background: rgba(255, 255, 255, 0.12) !important;
      border: none !important;
      box-shadow: none !important;
    }

    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: #ffffff !important;
      fill: #ffffff !important;
    }
  }

  .ant-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0 !important;
    margin: 0 !important;
    background: #ffffff;
  }
`;

const ApplicantDetailBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const ApplicantDetailHeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-right: 4px;
`;

const ApplicantDetailHeaderName = styled.div`
  color: #ffffff;
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const ApplicantDetailHeaderSub = styled.div`
  color: rgba(255, 255, 255, 0.82);
  font-weight: 500;
  font-size: 0.8125rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

const ApplicantDetailContent = styled.div`
  padding: 16px 20px 20px;

  @media ${device.mobile} {
    padding: 14px 16px 16px;
  }
`;

const ApplicantDetailDangerZone = styled.div`
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const ApplicantDetailStageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

const ApplicantDetailNoPhoto = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 160px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.875rem;

  .anticon {
    font-size: 28px;
    color: var(--color-primary, #2c5f2d);
  }
`;

const ApplicantCarouselWrap = styled.div`
  margin: ${(p) => (p.$hero ? "0" : "4px 0 12px")};
`;

const ApplicantCarouselFrame = styled.div`
  position: relative;
  border-radius: ${(p) => (p.$hero ? "0" : "10px")};
  overflow: hidden;
  background: #f1f5f9;
`;

const ApplicantCarouselImg = styled.img`
  display: block;
  width: 100%;
  height: ${(p) => (p.$hero ? "240px" : "168px")};
  object-fit: cover;
  cursor: zoom-in;

  @media ${device.mobile} {
    height: ${(p) => (p.$hero ? "200px" : "148px")};
  }
`;

const ApplicantCarouselDoc = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: ${(p) => (p.$hero ? "240px" : "168px")};
  margin: 0;
  padding: 1.25rem;
  border: none;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%);
  cursor: pointer;
  color: #334155;
  text-align: center;

  .anticon {
    font-size: 2rem;
    color: var(--color-primary, #2c5f2d);
  }

  @media ${device.mobile} {
    height: ${(p) => (p.$hero ? "200px" : "148px")};
  }
`;

const ApplicantCarouselDocType = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-primary-dark, #2c5f2d);
`;

const ApplicantCarouselDocName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  max-width: 90%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ApplicantCarouselDocHint = styled.span`
  font-size: 0.75rem;
  color: #64748b;
`;

const ApplicantCarouselBadge = styled.span`
  position: absolute;
  left: 10px;
  top: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.4;
  z-index: 1;
  pointer-events: none;
`;

const ApplicantCarouselNav = styled.button`
  position: absolute;
  top: 50%;
  ${(p) => (p.$side === "left" ? "left: 8px;" : "right: 8px;")}
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.55);
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 1;

  &:hover {
    background: rgba(15, 23, 42, 0.75);
  }

  .anticon {
    font-size: 12px;
    color: #ffffff;
  }
`;

const ApplicantCarouselCounter = styled.div`
  position: absolute;
  right: 10px;
  bottom: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
  z-index: 1;
`;

const ExpandPanelCue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d7e3d8;
  background: #f4f8f4;
  min-width: 0;
  color: #1f3d20;
  transition: background 0.15s ease, border-color 0.15s ease;

  ${ApplicantCardButton}:hover & {
    background: #eaf3ea;
    border-color: #b7cbb8;
  }
`;

const ExpandPanelCueIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--color-primary, #2c5f2d);
  color: #ffffff !important;
  font-size: 12.5px;

  .anticon,
  .anticon svg {
    color: #ffffff !important;
    font-size: 12.5px !important;
    width: 12.5px;
    height: 12.5px;
  }
`;

const ExpandPanelCueText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
  color: #1f3d20;
`;

const ExpandPanelCueArrow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #2c5f2d;
  font-size: 11px;
`;

const TenantActionsBand = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 12px 0 4px;
  margin-top: 8px;
  margin-bottom: 4px;
  min-width: 0;
  border-bottom: 1px solid #eef2f7;
  padding-bottom: 16px;

  .lease-up-muted-alert.ant-alert-info {
    background: #f8fafc;
    border: 1px solid #e5e7eb;
  }

  .lease-up-muted-alert .ant-alert-message,
  .lease-up-muted-alert .ant-alert-description {
    color: #1f2937;
  }

  .lease-up-muted-alert .ant-alert-icon {
    color: #6b7280;
  }

  .lease-up-coming-soon-alert.ant-alert-warning {
    background: #fffbeb;
    border: 1px solid #fde68a;
  }

  .lease-up-coming-soon-alert .ant-alert-message,
  .lease-up-coming-soon-alert .ant-alert-description {
    color: #92400e;
  }

  .lease-up-coming-soon-alert .ant-alert-icon {
    color: #d97706;
  }

  .ant-btn-primary .anticon {
    color: #ffffff !important;
  }

  .ant-btn-primary:disabled .anticon,
  .ant-btn-primary.ant-btn-disabled .anticon {
    color: rgba(0, 0, 0, 0.35) !important;
    filter: none !important;
  }
`;

const TenantDocumentsBand = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 16px 0 4px;
  margin-top: 0;
  margin-bottom: 0;
  min-width: 0;

  & + & {
    border-top: 1px solid #eef2f7;
    margin-top: 8px;
    padding-top: 16px;
  }
`;

const TenantCardTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

const TenantIdentityBlock = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const StageTag = styled(Tag)`
  margin: 0 !important;
  flex-shrink: 0;
  align-self: flex-start;
`;

const PipelineMiniTrack = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  gap: 6px;
  margin: 12px 0 0;
  padding: 12px 0 0;
  border-top: 1px solid #eef2f0;
  background: transparent;
  min-width: 0;

  @media ${device.mobile} {
    gap: 4px;
    margin-top: 10px;
    padding-top: 10px;
  }
`;

const PipelineMiniStep = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  opacity: ${(p) => (p.$done || p.$current ? 1 : 0.38)};
`;

const PipelineMiniIconWrap = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "$done" && prop !== "$current",
})`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  flex-shrink: 0;
  @media ${device.mobile} {
    width: 26px;
    height: 26px;
    font-size: 11.5px;
  }
  background: ${(p) =>
    p.$current || p.$done
      ? "var(--color-primary, #2c5f2d)"
      : "#f0f0f0"};
  color: ${(p) => (p.$current || p.$done ? "#ffffff" : "rgba(0,0,0,0.45)")};
  border: ${(p) =>
    p.$current || p.$done ? "none" : "1px solid #d9d9d9"};

  .anticon,
  .anticon svg {
    font-size: inherit !important;
    width: 1em;
    height: 1em;
  }
`;

const PipelineMiniLabel = styled.span`
  font-size: 10px;
  line-height: 1.15;
  color: rgba(0, 0, 0, 0.65);
  max-width: 100%;
  word-break: break-word;
  hyphens: auto;
`;

const StepSubLine = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$compact",
})`
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin: ${(p) => (p.$compact ? "4px 0 0" : "0 0 8px")};
  text-align: left;
`;

const NextStepLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
  margin: 0 0 10px;
`;

const TenantActionsArea = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .ant-btn {
    gap: 6px !important;
  }

  .ant-btn .ant-btn-icon {
    margin-inline-end: 0 !important;
  }
`;

const ActionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const LeaseStatusRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const OnboardStyledModal = styled(Modal)`
  --lo-modal-header-height: 56px;
  --lo-modal-close-size: 32px;

  &.ant-modal {
    padding-bottom: 0;
    max-width: calc(100vw - 24px);
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100%;
    max-height: calc(100dvh - 32px);
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-modal-header {
    flex-shrink: 0;
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 14px 52px 14px 20px;
    margin: 0;
    min-height: var(--lo-modal-header-height);
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }

  .ant-modal-title {
    color: white !important;
    font-family: var(--font-primary, inherit);
    font-weight: 700;
    font-size: 1.125rem;
    line-height: 1.25;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: 12px !important;
    right: 16px !important;
    width: var(--lo-modal-close-size) !important;
    height: var(--lo-modal-close-size) !important;
    padding: 0 !important;
    margin: 0 !important;
    transform: none !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;

    .anticon,
    .anticon svg,
    .ant-modal-close-x,
    .ant-modal-close-icon,
    svg {
      color: white !important;
      fill: white !important;
    }

    &:hover {
      color: white !important;
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
    }
  }

  .ant-modal-body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 18px 22px !important;
    background: #ffffff;
  }

  .ant-modal-footer {
    flex-shrink: 0;
    margin: 0;
    padding: 12px 22px 16px;
    border-top: 1px solid #eef0f2;
    background: #ffffff;

    .ant-btn {
      gap: 6px !important;
      font-weight: 600 !important;
    }

    .ant-btn .ant-btn-icon {
      margin-inline-end: 0 !important;
    }

    .ant-btn-primary:not(:disabled):not(.ant-btn-disabled) .anticon {
      color: #ffffff !important;
    }

    .ant-btn-primary:disabled,
    .ant-btn-primary[disabled],
    .ant-btn-primary.ant-btn-disabled {
      background: #e5e7eb !important;
      border-color: #d1d5db !important;
      color: rgba(0, 0, 0, 0.35) !important;
      box-shadow: none !important;
      cursor: not-allowed !important;
    }
  }

  .lease-onboard-uploader.ant-upload-wrapper .ant-upload.ant-upload-drag {
    height: auto !important;
    min-height: 0 !important;
    border-radius: 10px !important;
    background: #f8fafc !important;
    border: 1.5px dashed #d9e2dc !important;
    padding: 12px 14px !important;
  }

  .lease-onboard-uploader .ant-upload-drag-container {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 2px;
    text-align: center !important;
    padding: 0 !important;
  }

  .lease-onboard-uploader .ant-upload-drag-icon {
    margin: 0 !important;
    height: auto !important;
  }

  .lease-onboard-uploader .ant-upload-drag-icon .anticon {
    color: var(--color-primary) !important;
    font-size: 24px !important;
  }

  .lease-onboard-uploader .ant-upload-text {
    display: block !important;
    width: auto !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: transparent !important;
    border-radius: 0 !important;
    font-size: 0.8125rem !important;
    font-weight: 600 !important;
    line-height: 1.3 !important;
    color: rgba(0, 0, 0, 0.78) !important;
    text-align: center !important;
  }

  .lease-onboard-uploader .ant-upload-hint {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 0.75rem !important;
    line-height: 1.3 !important;
    color: rgba(0, 0, 0, 0.45) !important;
    text-align: center !important;
  }

  @media ${device.mobile} {
    --lo-modal-header-height: 48px;
    --lo-modal-close-size: 28px;

    .ant-modal-header {
      padding: 12px 48px 12px 16px;
    }

    .ant-modal-title {
      font-size: 1rem;
    }

    .ant-modal-body {
      padding: 14px 16px !important;
    }

    .ant-modal-footer {
      padding: 10px 16px 14px;
    }
  }
`;

const OnboardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const OnboardIntro = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.68);

  strong {
    color: rgba(0, 0, 0, 0.88);
    font-weight: 600;
  }
`;

const OnboardUploadBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OnboardUploadLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.72);
  margin: 0;
`;

const OnboardLeaseReady = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 12px 12px 14px;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.1) 0%,
    rgba(44, 95, 45, 0.04) 100%
  );
  border: 1.5px solid rgba(44, 95, 45, 0.45);
  box-sizing: border-box;
`;

const OnboardLeaseReadyIcon = styled.div`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary, #2c5f2d);
  color: #ffffff;
  font-size: 18px;
`;

const OnboardLeaseReadyMeta = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OnboardLeaseReadyTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-primary, #2c5f2d);
  line-height: 1.3;
`;

const OnboardLeaseReadyName = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.78);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OnboardLeaseRemoveBtn = styled(Button)`
  && {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    padding: 0;
    color: #6b7280;
  }

  &&:hover {
    color: #dc2626 !important;
    background: #fef2f2 !important;
  }
`;

const OnboardAckWrap = styled.div`
  margin: 4px 0 0;

  .ant-checkbox-wrapper {
    align-items: flex-start;
    gap: 8px;
    font-size: 0.8125rem !important;
    line-height: 1.45;
    color: rgba(0, 0, 0, 0.62);
    white-space: normal;
  }

  .ant-checkbox {
    margin-top: 2px;
  }

  .ant-checkbox .ant-checkbox-inner {
    border-color: #64748b !important;
    border-width: 1.5px !important;
    background: #ffffff !important;
  }

  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: #475569 !important;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background: var(--color-primary, #2c5f2d) !important;
    border-color: var(--color-primary, #2c5f2d) !important;
  }

  .ant-checkbox-wrapper:hover .ant-checkbox-checked .ant-checkbox-inner,
  .ant-checkbox-checked:hover .ant-checkbox-inner {
    background: var(--color-primary, #2c5f2d) !important;
    border-color: var(--color-primary, #2c5f2d) !important;
  }

  .ant-checkbox + span {
    padding-inline-start: 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    color: rgba(0, 0, 0, 0.62);
  }
`;

const OnboardStepsWrap = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #eef0f2;
`;

const OnboardStepsLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 10px;
`;

const OnboardStepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OnboardStepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${(p) =>
    p.$error
      ? "rgba(220, 38, 38, 0.06)"
      : p.$done || p.$active
      ? "rgba(44, 95, 45, 0.08)"
      : "#ffffff"};
  border: 1px solid
    ${(p) =>
      p.$error
        ? "rgba(220, 38, 38, 0.28)"
        : p.$done || p.$active
        ? "rgba(44, 95, 45, 0.28)"
        : "#e5e7eb"};
`;

const OnboardStepBadge = styled.div`
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  background: var(--color-primary, #2c5f2d);
  color: #ffffff;
  line-height: 1;
`;

const OnboardStepCopy = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const OnboardStepTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.84);
  line-height: 1.3;
`;

const OnboardStepDesc = styled.div`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
  line-height: 1.35;
`;
